import React, { useState, useEffect, useRef, useMemo } from "react";
import PdfPrinter from "pdfmake";
import toast from "react-hot-toast";
import { useBarcode } from "react-barcodes";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Alert, CircularProgress } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Card from "@mui/material/Card";
import Select from "@mui/material/Select";
import Components from "./index";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { connect } from "react-redux";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useOrganization } from "../../configurations/OrganizationContext";
import { useParams } from "react-router-dom";

import {
  patientActions,
  doctorActions,
  tokenActions,
} from "../../core/actions";
import { useDispatch, useSelector } from "react-redux";
import { sideBarNavItemsServices } from "../../data";
import { helperServices } from "../../shared/helper/index";
import MessageStrip from "../../shared/messagestrip/MessageStrips";
import { useHistory, useLocation } from "react-router-dom";
import { DOC_INFO, InsuranceDesk } from "../../shared/constants";
import { parse, utcToZonedTime } from "date-fns-tz";
import { PATIENT_SECTION, REG_INVOICE } from "../../shared/constants";
import { addMonths, isBefore, isSameDay, format } from "date-fns";
import { runningToken } from "../../core/actions/tokenActions";
import { Link } from "react-router-dom";
import { forEach, transform } from "lodash";
import personDetailsService from "../../core/services/personDetailsService";
import bookingplanService from "../../core/services/bookingplanService";
import subscriptionService from "../../core/services/subscriptionService";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { Loading } from "../erp-billing/Loading";
import erpActions from "../erp-billing/ERPActions/erpActions";
import { MASTER_LOCATION_DATA_LOCATION_KEY } from "../../core/constants/erpConstants";
import {
ORG_CODES
} from "../../shared/constants";

const PATIENT_SECTION_CARD_STYLE = {
  border: "1px solid #d6e3f0",
  borderRadius: "8px",
  boxShadow: "0 0 10px rgba(30, 55, 80, 0.12)",
  overflow: "hidden",
};

const PATIENT_PRIMARY_TITLE_STYLE = {
  color: "#174a7e",
  fontSize: "22px",
  lineHeight: 1.3,
};

const PATIENT_SECTION_TITLE_STYLE = {
  color: "#174a7e",
  fontSize: "18px",
  lineHeight: 1.3,
};

const PATIENT_META_TEXT_STYLE = {
  color: "#5f6b76",
  fontSize: "13px",
  lineHeight: 1.4,
  marginBottom: 0,
};

function MembershipUsageHistory({ patientId, visitId }) {
  const [usageHistory, setUsageHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fullHistoryOpen, setFullHistoryOpen] = useState(false);
  const [fullHistory, setFullHistory] = useState([]);
  const [fullHistoryLoading, setFullHistoryLoading] = useState(false);
  const [fullHistoryError, setFullHistoryError] = useState("");

  const fullHistoryColumnDefs = useMemo(() => [
    {
      headerName: "Service",
      field: "serviceName",
      filter: "agTextColumnFilter",
      minWidth: 220,
      flex: 1.5,
    },
    {
      headerName: "Quantity",
      field: "quantity",
      filter: "agNumberColumnFilter",
      minWidth: 130,
      flex: 0.7,
    },
    {
      headerName: "IP/OP Number",
      field: "ipopNumber",
      filter: "agTextColumnFilter",
      minWidth: 150,
      flex: 0.8,
      valueFormatter: (params) => params.value || "Not recorded",
    },
    {
      headerName: "Visit Date",
      field: "visitDate",
      filter: "agDateColumnFilter",
      minWidth: 150,
      flex: 0.8,
      valueFormatter: (params) => params.value
        ? format(new Date(params.value), "dd MMM yyyy")
        : "Date not recorded",
    },
    {
      headerName: "Consumed Date & Time",
      field: "consumedAtDisplay",
      filter: "agTextColumnFilter",
      minWidth: 230,
      flex: 1.2,
    },
  ], []);

  const fullHistoryDefaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    floatingFilter: true,
    headerClass: "membership-history-header-left",
  }), []);

  const openFullHistory = () => {
    setFullHistoryOpen(true);
    setFullHistoryLoading(true);
    setFullHistoryError("");

    subscriptionService
      .getPatientSubscriptionUsageHistory(patientId)
      .then((response) => {
        const history = Array.isArray(response) ? response : [];
        setFullHistory(history.map((usage) => ({
          ...usage,
          serviceName: usage.serviceName || `Service ${usage.serviceId}`,
          consumedAtDisplay: usage.consumedAt
            ? format(new Date(usage.consumedAt), "dd MMM yyyy, h:mm a")
            : "Date not recorded",
        })));
      })
      .catch((apiError) => {
        setFullHistory([]);
        setFullHistoryError(
          apiError?.message || apiError || "Unable to load complete membership usage history",
        );
      })
      .finally(() => setFullHistoryLoading(false));
  };

  useEffect(() => {
    if (!patientId || !visitId) return undefined;

    let active = true;
    setLoading(true);
    setError("");
    subscriptionService
      .getSubscriptionUsageHistory(patientId, visitId, page)
      .then((response) => {
        if (!active) return;
        setUsageHistory(response?.content || []);
        setTotalPages(response?.totalPages || 0);
        setTotalElements(response?.totalElements || 0);
      })
      .catch((apiError) => {
        if (!active) return;
        setError(apiError?.message || apiError || "Unable to load membership usage history");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [patientId, visitId, page]);

  if (error || (!loading && totalElements === 0)) return null;
  if (totalElements === 0) return null;

  return (
    <div className="row mt-5 pt-2">
      <div className="col-12">
        <Card
          className="p-3 mt-0"
          style={PATIENT_SECTION_CARD_STYLE}
        >
          <div
            className="d-flex align-items-center justify-content-between mb-3"
            style={{
              paddingBottom: "12px",
              borderBottom: "1px solid #e4edf6",
            }}
          >
            <div>
              <h6 className="mb-1 pl-0" style={PATIENT_SECTION_TITLE_STYLE}>
                Membership Usage History
              </h6>
              <small className="text-muted">
                Membership services redeemed during this visit
              </small>
            </div>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              {loading && <CircularProgress size={14} />}
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openFullHistory();
                }}
              >
                View Full History
              </Button>
              {/* <small className="text-muted">
                {totalElements} record{totalElements === 1 ? "" : "s"}
              </small> */}
            </div>
          </div>

          {usageHistory.length > 0 && (
            <>
              <div style={{ border: "1px solid #e0e0e0", borderRadius: "6px", overflow: "hidden" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(240px, 2fr) 140px minmax(220px, 1fr)",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 14px",
                    borderBottom: "1px solid #d5dbe0",
                    backgroundColor: "#f4f6f8",
                    fontSize: "12px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  <span>Service</span>
                  <span>Quantity</span>
                  <span>Consumed Date &amp; Time</span>
                </div>
                {usageHistory.map((usage) => (
                  <div
                    key={`membership-usage-${usage.id}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(240px, 2fr) 140px minmax(220px, 1fr)",
                      alignItems: "center",
                      gap: "12px",
                      padding: "11px 14px",
                      borderBottom: "1px solid #e0e0e0",
                      fontSize: "13px",
                    }}
                  >
                    <strong>{usage.serviceName || `Service ${usage.serviceId}`}</strong>
                    <span>{usage.quantity}</span>
                    <span className="text-muted">
                      {usage.consumedAt
                        ? format(new Date(usage.consumedAt), "dd MMM yyyy, h:mm a")
                        : "Date not recorded"}
                    </span>
                  </div>
                ))}
              </div>

              <div className="d-flex align-items-center justify-content-end mt-3" style={{ gap: "10px" }}>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  disabled={loading || page === 0}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setPage((currentPage) => Math.max(0, currentPage - 1));
                  }}
                >
                  Previous
                </Button>
                <small className="text-muted">
                  Page {page + 1} of {totalPages}
                </small>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  disabled={loading || page + 1 >= totalPages}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setPage((currentPage) => currentPage + 1);
                  }}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      <Dialog
        open={fullHistoryOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setFullHistoryOpen(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ style: { maxHeight: "86vh" } }}
      >
        <DialogTitle style={{ padding: "18px 24px 8px" }}>
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div style={PATIENT_SECTION_TITLE_STYLE}>Complete Membership Usage History</div>
              <small className="text-muted">
                Membership services redeemed by this patient across all visits
              </small>
            </div>
            <IconButton
              aria-label="Close complete membership history"
              size="small"
              onClick={() => setFullHistoryOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers style={{ padding: "18px 24px" }}>
          {fullHistoryLoading && (
            <div className="d-flex align-items-center justify-content-center" style={{ height: "430px" }}>
              <CircularProgress size={30} />
            </div>
          )}
          {!fullHistoryLoading && fullHistoryError && (
            <Alert severity="error">{fullHistoryError}</Alert>
          )}
          {!fullHistoryLoading && !fullHistoryError && (
            <div className="membership-history-grid">
              <style>{`
                .membership-history-grid .membership-history-header-left .ag-header-cell-label {
                  justify-content: flex-start !important;
                  text-align: left !important;
                }
              `}</style>
              <div
                className="ag-theme-balham ag-style card rounded table mb-0 tcol"
                style={{ height: "430px", width: "100%", fontSize: "13px" }}
              >
                <AgGridReact
                  rowData={fullHistory}
                  columnDefs={fullHistoryColumnDefs}
                  defaultColDef={fullHistoryDefaultColDef}
                  pagination
                  paginationPageSize={10}
                  animateRows
                  suppressCellSelection
                  overlayNoRowsTemplate="No membership usage history found"
                />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "10px 24px 14px" }}>
          <Button type="button" onClick={() => setFullHistoryOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}


function PatientSections(props) {
  const org = useOrganization();
  const isVedamaayu =
    org?.orgCode?.toLowerCase() === ORG_CODES.VEDAMAAYU;
  const { patientId, visitId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const istTimeZone = "Asia/Kolkata";
  const isMountedRef = useRef(true);
  const [loader, setLoader] = useState(true);
  const [allAssignedRooms, setAllAssignedRooms] = useState([]);
  const [showMsgStrip, setShowMsgStrip] = useState(false);
  const [messageDetails, setMessageDetails] = useState({});
  const [collapseBasicInfo, setCollapseBasicInfo] = useState(false);
  const [collapseAttachment, setCollapseAttachment] = useState(false);
  const [collapseComplaints, setCollapseComplaints] = useState(false);
  const [uploadPreview, setuploadPreview] = useState(false);
  const [registerPdfname, setRegisterPdfname] = useState("");
  const [patientRegInfo, setPatientRegInfo] = useState([]);
  const [collapseDoctorList, setCollapseDoctorList] = useState(false);
  const [collapseReference, setCollapseReference] = useState(false);
  const [collapseBarcode, setCollapseBarcode] = useState(false);
  const [collapseRoom, setCollapseRoom] = useState(false);
  const [collapseInsurance, setCollapseInsurance] = useState(false);
  const [callType, setCallType] = useState(false);
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState("");
  const [deskDetails, setDeskDetails] = useState({});
  const [updateTokenDashboard, setupdateTokenDashboard] = useState(false);
  const [onBoardmodal, setOnBoardModal] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState("OutPatient");
  const [patientCardPrint, setPatientCardPrint] = useState(false);
  const [surgeryConsentPrint, setSurgeryConsentPrint] = useState(false);
  const [panchkarmaConsentPrint, setPanchkarmaConsentPrint] = useState(false);
  const [admissionConsentPrint, setAdmissionConsentPrint] = useState(false);
  const [painAssessmentPrint, setPainAssessmentPrint] = useState(false);
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [selectedMembershipPlanId, setSelectedMembershipPlanId] = useState("");
  const [membershipReceiptNo, setMembershipReceiptNo] = useState("");
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [hasActiveMembership, setHasActiveMembership] = useState(false);
  //const [profileLoading, setProfileLoading] = useState(false);


  const [patientInfo, setPatientInfo] = useState({
    visitId: "",
    patientId: "",
    name: "",
    age: "",
    phno: "",
    id: "",
    address: "",
    adhar: "",
    age: "",
    area: "",
    bloodgroup: "",
    city: "Arkula",
    state: "Karnataka",
    lname: "",
    mname: "",
    fname: "",
    occupation: "",
    patientcode: "",
    pincode: "",
    admsnDate: "",
    dischargeDate: "",
    type: "OutPatient",
    fileNo: "",
    email: "",
    srcRef: "",
    language: "",
    education: "",
    sociostatus: "",
    tokenNumber: "",
    deskNumber: "",
    comments: "",
    status: "",
    ehrid: "",
    erpCustId: "",
    altPhno: "",
    visitDate: "",
    insurance: "",
    registeredOn: "",
    regFeesPdfUrl: "",
    emrPhono: "",
    gender: "",
    internalStatus: "",
    visitflag: "",
    custGroupId: "",
    fathern: "",
    mothern: "",
    fathernorspouse: "",
    maskAdhar: "",
    department: "",
    dob: "",
    fullName: "",
    ipopnumber: "",
    visit: {
      reference: {
        refby: "",
        refkey: "",
      },
      referenceId: "",
    },
  });
  const isWalkinVisit =
    (patientInfo.visitflag || "").toLowerCase() === "walkin";
  const [currentDoctorName, setCurrentDoctorName] = useState("—");
  const [currentCounselorName, setCurrentCounselorName] = useState("—");
  const [regDate, setRegDate] = useState("");
  const [patientProcedureList, setPatientProcedureList] = useState({});
  const [fileUploadcontent, setfileUploadcontent] = useState();
  const [patientVisitList, setPatientVisitList] = useState();
  const dispatch = useDispatch();
  const patient = useSelector((state) => state.patientDetailsState.patient);
  const dPatient = useSelector((state) => state.patientDetailsState.dPatient);
  const patientDetails = useSelector(
    (state) => state.patientDetailsState.patientDetail
  );
  const doctInfo = useSelector((state) => state.doctorDetailsState.docInfo);
  const [lastAsssigned, setLastAssigned] = useState({});
  const [docInfo, setdocInfo] = useState({});
  const [counsInfo, setcounsInfo] = useState({});
  const [ServerRes, setServerRes] = useState({ success: "", error: "" });
  const [selectedDate, setSelecetdDate] = useState(new Date());
  const [allvisitLists, setAllvisitLists] = useState([]);
  const [allVisitdates, setallVisitdates] = useState([]);
  const [activeSection, showactivesection] = useState([]);
  const [showPayment, setShowPayment] = useState([]);
  const [apiCall, setApiCall] = useState(false);
  const [registrationCard, setRegistrationCard] = useState(false);
  const [printTOkenModal, setPrintTOkenModal] = useState(false);
  const [separatePdf, SetSeparatePdf] = useState(false);
  const [PrintFiles, setPrintFiles] = useState(false);
  const [mergePdf, SetMergePdf] = useState(false);
  const [summary, setSummary] = useState({
    patientId: "",
    visitId: "",
    groupname: "",
    shortdesc: "",
    longdesc: "",
    targetid: "",
  });
  const [arrayui, setarrayui] = useState([]);
  const [complaintText, setComplaintText] = useState([]);
  const [selectedVisit, setSelecetdDateVisit] = useState();
  const [patientCalenderInfoD, setpatientCalenderInfoD] = useState({});
  const [patientCalenderInfoC, setpatientCalenderInfoC] = useState({});
  const [currentStatus, setCurrentStatus] = useState(null);
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const [masterLocationDetails, setMasterLocationDetails] = useState(null);
  const [locationId, setLocationId] = useState(null);
  // Get the current time and format it as "HH:mm" for the time input field
  const currentTime = format(new Date(), "HH:mm");
  const [selectedVisitId, setSelectedVisitId] = useState("");

  // Set the default values
  const [selecteddate, setSelecteddate] = useState(todayDate);
  const [selectedTime, setSelectedTime] = useState(currentTime);
  const [isOverrided, setIsOverrided] = useState(false);
  const [patientDetail, setPatientDetail] = useState({
    patientId: "",
    visitId: "",
    closecomment: "",
    closeScheduledTime: "",
  });
  const [internalStatusOut, setInternlStatusOut] = useState();
  const [onBoardupdate, setonBoardupdate] = useState(false);
  const [customerGroup, setCustomerGroup] = useState([]);
  const [desc, setdesc] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [patientCloseModal, setPatientCloseModal] = useState(false);

  const [departmentList, setDepartmentList] = useState([]);
  const [apptdate, setapptdate] = useState(false);
  const [errorMessages, setErrorMessages] = useState("");
  const showCounsellorAppointment =
    patientInfo?.status !== "preReg" &&
    org?.isCounsellor?.display &&
    org?.orgCode !== ORG_CODES.VEDAMAAYU;
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.state?.refreshRoom && visitId) {
      console.log("[PatientSections] Refreshing data after booking plan update");
      // Refresh room assignments
      getAssignedAllRoomlist(visitId);

      // Refresh session details (includes cancelled sessions, unlike room list)
      const patIdForSessions = props.match?.params?.patientId;
      if (patIdForSessions) {
        getPatientSessionDetails(patIdForSessions, visitId);
      }

      // Also refresh patient profile and visit list with the current visitId
      // to ensure doctor name and room data are up-to-date in SearchDocList
      const patId = props.match?.params?.patientId;
      if (patId) {
           getVisitList(patId, visitId);
       // getPatientProfile(patId, visitId);  
        //getVisitList(patId, visitId); 
      }
      
      // Clear the refresh flag to prevent repeated refreshes
      history.replace({
        ...location,
        state: { ...location.state, refreshRoom: undefined }
      });
    }
  }, [location.state?.refreshRoom, visitId]);

  useEffect(() => {
    isMountedRef.current = true;

    const initializeComponent = async () => {
      try {
        if (!isMountedRef.current) return;

        getCustomerGroupInfo();
        getDepartmentList();
        dispatch(patientActions.setPage(PATIENT_SECTION));
        console.log("patient in patientsections: ", patient);
        var role = JSON.parse(localStorage.getItem("role"));
        if (isMountedRef.current) {
          setRole(role);
        }
        console.log("useEffect :", role);

        let patId = props.match.params.patientId;
        let vId = props.match.params.visitId;
        //procedure list api
        if (patId && patId != "" && vId && vId != "") {
          getPatientProfile(patId, vId);
          getProcedureList(patId, vId);
          // SearchDocList reads the current doctor from the visit response kept
          // in Redux. Fetch it on a normal PatientSections page load as well;
          // previously this only happened after selected refresh/actions.
          getVisitList(patId, vId);
          if (isMountedRef.current) {
            setCallType("Edit");
          }
        } else {
          if (isMountedRef.current) {
            setCallType("Add");
          }
        }
        getAssignedAllRoomlist(vId);
        getPatientSessionDetails(patId, vId);
        getDoctorList();

        console.log("Patid", patId);
        console.log("vId:", vId);

        const visitIdFromUrl = props.match?.params?.visitId;

        if (visitIdFromUrl) {
          setSelecetdDate(visitIdFromUrl);
        }

        if (role && isMountedRef.current) {
          const roles = role.split(",");
          setRoles(roles);
        }

        let all_roles = helperServices.getRoles();
        console.log("allroles", all_roles);
        Object.keys(all_roles).forEach((key) => {
          console.log("allroles key", key);
          console.log("currentRole", role);
          if (key === role && isMountedRef.current) {
            console.log("all_roles[key]:", all_roles[key]);
            showactivesection([...all_roles[key]]);
            setTimeout(() => {
              if (isMountedRef.current) {
                console.log("activesection key:", activeSection);
              }
            }, 500);
          }
        });
      } catch (error) {
        console.error("Error in PatientSections useEffect:", error);
        if (isMountedRef.current) {
          setServerRes({ error: "Failed to initialize component" });
        }
      }
    };

    initializeComponent();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, [onBoardupdate]);


  useEffect(() => {
    const visitIdFromUrl = props.match?.params?.visitId;
    const patientIdFromUrl = props.match?.params?.patientId;

    if (visitIdFromUrl) {
      setSelecetdDate(visitIdFromUrl);
      if (patientIdFromUrl) {
        getPatientSessionDetails(patientIdFromUrl, visitIdFromUrl);
      }
    }
  }, [props.match.params.visitId, props.match.params.patientId]);

  useEffect(() => {
    if (patientInfo?.id && patientInfo?.visitId) {
      getLocationIdByVisitType();
    }
  }, [patientInfo?.type, patientInfo?.id, patientInfo?.visitId]);

  const getCustomerGroupInfo = () => {
    dispatch(
      patientActions.getCustomerGroupInfo((resCb) => {
        if (resCb && isMountedRef.current) {
          console.log("Rescb", resCb);
          const list = resCb.customerGroupInfo || [];
          setCustomerGroup([...list]);
          const matchedGroup = list.find(
            (group) => group.custGroupId == patientInfo.custGroupId
          );

          const description = matchedGroup?.description || "";

          setdesc(description);
          patientProcedureList.custGroupId = patientInfo.custGroupId;
          patientProcedureList.custGrpDesc = description;
        }
      })
    );
  };
  const getMasterlocationDetails = async () => {
    let masterLocDetails = [];
    if (!masterLocationDetails) {
      masterLocDetails = await erpActions.getAllMasterLocationDetails(dispatch);
      setMasterLocationDetails(masterLocDetails);
    } else {
      masterLocDetails = masterLocationDetails;
    }
    return masterLocDetails;
  }

  const getLocationIdByVisitType = async () => {
    const masterLocDetails = await getMasterlocationDetails();
    const isOutPatient = patientInfo?.type === "OutPatient";
    const locationKey = isOutPatient
      ? MASTER_LOCATION_DATA_LOCATION_KEY.OP_BILLING
      : MASTER_LOCATION_DATA_LOCATION_KEY.IP_BILLING;

    const locId = helperServices.getFirstLocationIdByKey(masterLocDetails, locationKey);
    setLocationId(locId);
    return locId;
  }
  const getDepartmentList = () => {
    dispatch(
      doctorActions.getDepartmentList({}, (resCb) => {
        if (resCb && isMountedRef.current) {
          setDepartmentList(resCb);
        }
      })
    );
  };

  const getDepartmentName = (deptId) => {
    if (!deptId) return "";
    const dept = departmentList.find((d) => d.id == deptId);
    return dept ? dept.name : "";
  };

  const getCalenderForPatient = (gvId, pateintId, type) => {
    let data = { gvid: gvId, patientId: pateintId };
    console.log("data: ", data);
    dispatch(
      patientActions.getDoctorCalenderForPatient(data, (rescb) => {
        if (rescb && isMountedRef.current) {
          if (type.toLowerCase() === "doctor") {
            console.log("resCb Patient Calender", rescb);
            setpatientCalenderInfoD(rescb.calenderForPatient);
          } else {
            console.log("resCb Patient Calender", rescb);
            setpatientCalenderInfoC(rescb.calenderForPatient);
          }
        }
      })
    );
  };

  const getDoctorList = () => {
    let data = {};
    const resCb = personDetailsService.getDoctorsDetail();
    // dispatch(
    // doctorActions.getDoctorList(data, (resCb) => {
    if (resCb) {
      console.log("resCb doctor Response:", resCb);
      //code to sort the doctor name list in alpahbatical order
      const sort_rescb = Object.values(resCb);
      console.log("sort_rescb", sort_rescb);
      //resCb = sort_rescb.sort((a, b) =>
      //  a.first_name && a.first_name > b.first_name && b.first_name ? 1 : -1
      //);
      //sort_rescb.sort((a, b) =>
      // a.first_name && a.first_name <b.first_name && b.first_name ? 1 : -1

      sort_rescb.sort((a, b) => {
        if (a && a.first_name && b && b.first_name) {
          return a.first_name.localeCompare(b.first_name);
        }
      });
      console.log("after_sort", sort_rescb);

      console.log("resCb: sorted", resCb);

      // },400)
    }
    // })
    // );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date
      .toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  };

  const getPatientProfile = (patId, vId) => {
    dispatch(
      patientActions.getPatientProfile(patId, (resCb) => {
        // ----------------------------------------
        // ❌ ERROR FROM BACKEND (EHR decrypt error)
        // ----------------------------------------
        console.log("getPatientProfile resCb:", resCb);
        if (
          !resCb ||
          resCb.respStatus === "0" ||
          resCb.respStatus === 0 ||
          resCb.errorMessages?.length > 0
        ) {
          const errorMsg =
            resCb?.errorMessages?.[0] ||
            "Failed to load patient data. Please try again.";

          if (isMountedRef.current) {
            setErrorMessages(errorMsg);
          }
        }

        // ----------------------------------------
        // ✔ SUCCESS RESPONSE
        // ----------------------------------------
        if (resCb && isMountedRef.current) {
          console.log("patientprofile", resCb);

          if (resCb?.visit && resCb?.visit?.id == vId && resCb?.visit?.patientId == patId) {
            addPatientProfileDetails(vId, patId, resCb);
          }
          getCustomerGroupInfo();
          dispatch(patientActions.setPatientDetails(resCb));

          if (resCb.visit && resCb.visit.status !== "close") {
            setCurrentStatus(resCb.visit.status);
          }

       //   setProfileLoading(false); 

          // ----------------------------------------
          // ✔ SAFE visitList HANDLING
          // ----------------------------------------
          if (Array.isArray(resCb.visitList) && resCb.visitList.length > 0) {
            // Safe version of reverse
            setallVisitdates([...resCb.visitList].reverse());
            console.log("visitList is:", resCb.visitList);

            // ----------------------------------------
            // ✔ MULTIPLE VISITS LOGIC
            // ----------------------------------------
            if (resCb.visitList.length > 1) {
              // Avoid crash if lastRegisteredOn missing
              if (resCb.lastRegisteredOn) {
                let lastVisit = sixMonthsPrior(resCb.lastRegisteredOn);
                setInternlStatusOut(lastVisit);
              }

              // Safe clone
              let visitList = [...resCb.visitList];

              visitList.shift(); // remove first item

              let lastvisitId = visitList[0]?.id; // safe

              if (lastvisitId) {
                let type = "showAllVisits";

                dispatch(
                  patientActions.getVisitListById(lastvisitId, type, (r) => {
                    if (r && isMountedRef.current) {
                      let lastassigned = { doctor: "", counsellor: "", room: "" };
                      let visit = r?.patsearchreslt?.[0];

                      // This previous visit fetch is used only to compute last assigned doctor/counsellor.
                      // Do not overwrite current visit-specific UI state with older visit data.
                      const personLookup = {};
                      if (r?.personList) {
                        r.personList.forEach((p) => {
                          personLookup[p.personId] = p;
                        });
                      }

                      // Fallback: use visit.person (initial doctor)
                      if (visit?.person) {
                        lastassigned.doctor =
                          (visit.person.displaytitle || "") +
                          "." +
                          (visit.person.first_name || "") +
                          " " +
                          (visit.person.last_name || "");
                        lastassigned.doctorId = visit.person.personId || visit.person.gvId || visit.person.id;
                      }

                      if (visit?.counsellor) {
                        lastassigned.counsellor =
                          (visit.counsellor.displaytitle || "") +
                          "." +
                          (visit.counsellor.first_name || "") +
                          " " +
                          (visit.counsellor.last_name || "");
                        lastassigned.counsellorId = visit.counsellor.personId || visit.counsellor.gvId || visit.counsellor.id;
                      }

                      if (visit?.room) {
                        lastassigned.room =
                          visit.room.roomName ||
                          visit.room.name ||
                          visit.room.label ||
                          (visit.room.id ? `Room ${visit.room.id}` : "");
                        lastassigned.roomId = visit.room.id || visit.room.roomId || visit.room.roomid;
                      }

                      // Override with the most recent Primary Doctor/Counsellor from visitAssignmentList
                      const allAssignments = visit?.visitAssignmentList || [];

                      // Most recent Primary Doctor
                      // Support both groupname="Primary Doctor" and type="Doctor" with groupname=null
                      // Also include groupname="new session" (saved by BookingNewSession)
                      const latestDoctorAssignment = [...allAssignments]
                        .reverse()
                        .find(
                          (a) =>
                            (a.groupname == null ||
                              a.groupname === "Primary Doctor" ||
                              a.groupname === "new session") &&
                            a.targetid &&
                            a.status === "open"
                        );
                      if (latestDoctorAssignment) {
                        const p = personLookup[latestDoctorAssignment.targetid];
                        if (p) {
                          lastassigned.doctor =
                            (p.displaytitle || "") +
                            "." +
                            (p.first_name || "") +
                            " " +
                            (p.last_name || "");
                          lastassigned.doctorId = p.personId || p.gvId;
                        }
                      }

                      const latestRoomAssignment = [...allAssignments]
                        .reverse()
                        .find(
                          (a) =>
                            ((a.groupname || "").toLowerCase() === "room" || (a.type || "").toLowerCase() === "room") &&
                            (a.targetid || a.targetname)
                        );
                      if (latestRoomAssignment) {
                        lastassigned.room =
                          latestRoomAssignment.targetname ||
                          (latestRoomAssignment.targetid ? `Room ${latestRoomAssignment.targetid}` : "");
                        lastassigned.roomId = latestRoomAssignment.targetid;
                      }

                      // Most recent Primary Counsellor
                      const latestCounsellorAssignment = [...allAssignments]
                        .reverse()
                        .find(
                          (a) =>
                            a.groupname === "Primary Counsellor" &&
                            a.targetid &&
                            a.status === "open"
                        );
                      if (latestCounsellorAssignment) {
                        const p = personLookup[latestCounsellorAssignment.targetid];
                        if (p) {
                          lastassigned.counsellor =
                            (p.displaytitle || "") +
                            "." +
                            (p.first_name || "") +
                            " " +
                            (p.last_name || "");
                          lastassigned.counsellorId = p.personId || p.gvId;
                        }
                      }

                      setLastAssigned({ ...lastassigned });
                    }
                  })
                );
              }
            }
          } else {
            // visitList missing → avoid crash
            setallVisitdates([]);
          }
        }
      })
    );

    scrollToElement();
  };

  const sixMonthsPrior = (date) => {
    let v;
    var d = new Date(date);
    var t = new Date();
    const diffYears = t.getFullYear() - d.getFullYear();
    const diffMonths = t.getMonth() - d.getMonth();
    const totalMonths = diffYears * 12 + diffMonths;
    if (totalMonths >= 6) {
      v = true;
    } else {
      v = false;
    }
    return v;
  };

  const scrollToElement = () => {
    if (patient.typeFlag != "all") {
      setTimeout(() => {
        if (!isMountedRef.current) return;

        var doc = document.getElementById("form");
        console.log("doc:", doc);
        if (doc && doc.childNodes) {
          for (let i = 0; i < doc.childNodes.length; i++) {
            console.log("doc.childNodes[i]:", doc.childNodes[i]);

            if (
              doc.childNodes[i].id &&
              doc.childNodes[i].id == patient.typeFlag
            ) {
              doc.childNodes[i].scrollIntoView(true);
              doc.childNodes[i].scrollIntoView({
                block: "start",
                inline: "nearest",
                behavior: "smooth",
                alignToTop: false,
              });

              doc.childNodes[i].style.paddingTop = "70px";
            }
          }
        }
      }, 500);
    }
  };

  const getProcedureList = (patId, vId) => {
    let type = "showAllVisits";
    console.log("-------PROCEDURE LIST API----------");
    dispatch(
      patientActions.getPatientProcedureInfo(patId, vId, type, (resCb) => {
        if (resCb && isMountedRef.current) {
          console.log("procedure list in api: ", resCb.patientresp);
          console.log("procedurelist", resCb);
          getCustomerGroupInfo();
          setPatientProcedureList(resCb.patientresp);
          if (resCb && resCb.patientresp && resCb.patientresp.procedureslist) {
            let itemFound = false;
            let check = false;
            let override = false;
            for (let i = 0; i < resCb.patientresp.procedureslist.length; i++) {
              const item = resCb.patientresp.procedureslist[i];
              if (
                item &&
                (item.groupname === "Registration Fees" ||
                  item.groupname === "ONBOARD_OUT_TO_IN" ||
                  item.groupname == "REVISIT" ||
                  item.groupname === "CONVERTTOINPATIENT")
              ) {
                console.log("patient procedure list in patientsection: ", item);
                check = true;
              } else if (item && item.groupname === "REVISIT_ONBOARD") {
                console.log("patient procedure list in patientsection: ", item);
                check = false;
              } else if (item && item.groupname === "BABY_ONBOARD") {
                console.log("patient procedure list in patientsection: ", item);
                check = true;
              } else if (item && item.groupname === "Overrided") {
                console.log("patient procedure list in patientsection: ", item);
                override = true;
              }
            }

            if (check == true) {
              console.log("Item not found");
              setIsOverrided(false);
            } else {
              console.log("Item found");
              setIsOverrided(true);
            }
          }
          addPatientDetails(vId, patId, resCb);
          listComplaints(resCb.patientresp);

          dispatch(doctorActions.setPatientProcedureList(resCb.patientresp));
          console.log(
            "patient procedure list in patientsection: ",
            resCb.patientresp
          );
          console.log("patientInfo in patientsection: ", patientInfo);
        }
      })
    );
    setTimeout(() => {
      if (isMountedRef.current) {
        toggleBasicInfo();
      }
    }, 500);
  };

  const listComplaints = (resp) => {
    setComplaintText([]);
    console.log("complaint list in api: ", resp);
    //procedureList = patientProceduresList.procedureslist
    let procedureList = resp.procedureslist;
    let patientProceduresList = resp;

    console.log("procedureList:", procedureList);
    console.log("patientProcedureList:", patientProceduresList);
    if (procedureList) {
      let list = [];
      for (let proc of procedureList) {
        let complaintList = {};
        console.log("procedures complaint 45:", proc);
        if (proc.claSS == "complaint") {
          console.log("procedures complaint 55:", proc.complaint);
          if (
            patientProceduresList.visit.id &&
            patientProceduresList.visit.id == proc.visitId &&
            proc.complaint
          ) {
            console.log(
              "procedures 1: ",
              patientProceduresList.visit.id,
              "procedures complaint 1: ",
              proc.visitId,
              "visit id in patient and procedure :",
              proc.class,
              " complaint in loop proc : ",
              proc.complaint
            );
            // props.patientInfo.
            complaintList["id"] = proc.id;
            complaintList["text"] = proc.complaint;
            complaintList["edit"] = false;
            complaintList["cdt"] = proc.cdt;
            list.push(complaintList);
          }
        }
      }
      if (list.length > 0) {
        setComplaintText([...list]);
      }
      console.log("complaintlist : ", list);
    }
  };
  const formatDate = (dateString) => {
    let date = dateString.split("T");
    let time = "";
    let timePart = dateString.split("T")[1].split(".")[0]; // Extract time part and remove milliseconds
    let timeSplit = timePart.split(":");
    time =
      format(new Date(date[0]), "MMM dd yyyy") +
      " " +
      timeSplit[0] +
      ":" +
      timeSplit[1];
    return time;
  };
  //It calls after getProcedureList method
  const addPatientDetails = (vId, patId, resCb) => {
    console.log("person data in add patient:", resCb);
    console.log("person data in add patient:", vId, patId);
    // resCb.patientresp.visit.status =  "close";
    let personData = {
      visitId: vId,
      patientId: patId,
      fname: resCb.patientresp.fname,
      fullName: getFullName(
        resCb?.patientresp?.fname,
        resCb?.patientresp?.mname,
        resCb?.patientresp?.lname
      ),
      age: resCb.patientresp.age,
      phno: resCb.patientresp.phno ? resCb.patientresp.phno : "",
      altPhno: resCb.patientresp.altPhno ? resCb.patientresp.altPhno : "",
      id: resCb.patientresp.id ? resCb.patientresp.id : "",
      address: resCb.patientresp.address ? resCb.patientresp.address : "",
      // adhar: resCb.patientresp.adhar ? resCb.patientresp.adhar : "",
      adhar: resCb.patientresp.maskAdhar ? resCb.patientresp.maskAdhar : "",
      maskAdhar: resCb.patientresp.maskAdhar ? resCb.patientresp.maskAdhar : "",
      area: resCb.patientresp.area ? resCb.patientresp.area : "",
      bloodgroup: resCb.patientresp.bloodgroup
        ? resCb.patientresp.bloodgroup
        : "",
      city: resCb.patientresp.city ? resCb.patientresp.city : "",
      lname: resCb.patientresp.lname ? resCb.patientresp.lname : "",
      mname: resCb.patientresp.mname ? resCb.patientresp.mname : "",
      fathern: resCb.patientresp.fathern ? resCb.patientresp.fathern : "",
      mothern: resCb.patientresp.mothern ? resCb.patientresp.mothern : "",
      fathernorspouse: resCb.patientresp.fathernorspouse
        ? resCb.patientresp.fathernorspouse
        : "",
      occupation: resCb.patientresp.occupation
        ? resCb.patientresp.occupation
        : "",
      patientcode: resCb.patientresp.patientcode
        ? resCb.patientresp.patientcode
        : "",
      pincode: resCb.patientresp.pincode ? resCb.patientresp.pincode : "",
      dob: resCb.patientresp.dob ? resCb.patientresp.dob : "",
      gender: resCb.patientresp.gender ? resCb.patientresp.gender : "",
      state: resCb.patientresp.state ? resCb.patientresp.state : "",
      email: resCb.patientresp.email ? resCb.patientresp.email : "",
      admsnDate: resCb.patientresp?.visit?.admsnDate
        // ? format(
        //     new Date(resCb.patientresp?.visit?.admsnDate),
        //     "MMM dd yyyy HH:mm"
        //   )
        // : "",
        ? (() => {
          const raw = resCb.patientresp.visit.admsnDate;
          const formatted = format(
            utcToZonedTime(new Date(raw), "Asia/Kolkata"),
            //vinay-Blank screnn issue ,chnaged from 12hr foramt to 24 hr format
           // "MMM dd yyyy hh:mm a"
            "MMM dd yyyy HH:mm"
          );
          console.log("[PatientSections] admission date from API (after onboard): raw=", raw, "| displayed (IST)=", formatted);
          return formatted;
        })()
        : "",

      dischargeDate: resCb.patientresp?.visit?.dischargeDate
        ? format(
          utcToZonedTime(
            new Date(resCb.patientresp.visit.dischargeDate),
            "Asia/Kolkata"
          ),
          "MMM dd yyyy hh:mm a"
        )
        : "",
      type: resCb.patientresp.visit
        ? resCb.patientresp.visit.type
        : "InPatient",
      department: resCb.patientresp.visit
        ? resCb.patientresp.visit.department
        : "",

      insurance:
        resCb.patientresp.visit && resCb.patientresp.visit.insurance
          ? resCb.patientresp.visit.insurance
          : "NO",
      fileNo: resCb.patientresp.fileNo ? resCb.patientresp.fileNo : "",
      education: resCb.patientresp.education ? resCb.patientresp.education : "",
      srcRef: resCb.patientresp.srcRef ? resCb.patientresp.srcRef : "",
      language: resCb.patientresp.language ? resCb.patientresp.language : "",
      sociostatus: resCb.patientresp.sociostatus
        ? resCb.patientresp.sociostatus
        : "",
      tokenNumber: resCb.patientresp.tokenNumber
        ? resCb.patientresp.tokenNumber
        : "",
      deskNumber: resCb.patientresp.deskNo ? resCb.patientresp.deskNo : "",
      comments: resCb.patientresp.comments ? resCb.patientresp.comments : "",
      status: resCb.patientresp?.visit?.status
        ? resCb.patientresp.visit.status
        : "",
      ehrid: resCb.patientresp.ehrid ? resCb.patientresp.ehrid : "",
      erpCustId: resCb.patientresp.erpCustId ? resCb.patientresp.erpCustId : "",
      custGroupId: resCb.patientresp.custGroupId
        ? resCb.patientresp.custGroupId
        : "",
      visitDate: resCb.patientresp.visit
        ? resCb.patientresp.visit.visitDate
        : "",
      internalStatus: resCb.patientresp.visit
        ? resCb.patientresp.visit.internalStatus
        : "",
      registeredOn: resCb.patientresp.registeredOn
        ? resCb.patientresp.registeredOn
        : "",
      emrPhono: resCb.patientresp.emrPhono ? resCb.patientresp.emrPhono : "",
      regFeesPdfUrl: patientInfo.regFeesPdfUrl,
      dob: resCb.patientresp.dob ? resCb.patientresp.dob : "",
      ipopnumber: resCb.patientresp?.visit?.ipopnumber || "",
    };
    //const registrationDate = new Date(resCb.registeredOn);
    setRegDate(
      resCb.patientresp.registeredOn
        ? format(new Date(resCb.patientresp.registeredOn), "MMM dd yyyy")
        : ""
    );
    console.log("pateintInfo in patientsection: ", patientInfo);
    console.log("person in patientsection: ", personData);
    const keys = Object.keys(patientInfo);
    const item = Object.keys(personData);
    var itmData = { ...personData };

    for (let itm of item) {
      for (let key of keys) {
        if (itm === key) {
          console.log("patientDetail : ", itmData[itm]);
          patientInfo[key] = itmData[itm];
        }
      }
    }

    getAssignedRoom(personData);

    console.log("pateintInfo in patientsection: ", patientInfo);
  };

  const formatName = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  //It calls after getPatientProfile method
  const addPatientProfileDetails = (vId, patId, resCb) => {
    console.log("person data in add patient:", resCb);
    console.log("person data in add patient:", vId, patId);
    let personData = {
      visitId: vId,
      patientId: patId,
      fname: resCb.fname,
      fullName: getFullName(resCb?.fname, resCb?.mname, resCb?.lname),
      age: resCb.age,
      phno: resCb.phno ? resCb.phno : "",
      altPhno: resCb.altPhno ? resCb.altPhno : "",
      id: resCb.id ? resCb.id : "",
      address: resCb.address ? resCb.address : "",
      // adhar: resCb.adhar ? resCb.adhar : "",
      adhar: resCb.maskAdhar ? resCb.maskAdhar : "",
      maskAdhar: resCb.maskAdhar ? resCb.maskAdhar : "",
      area: resCb.area ? resCb.area : "",
      bloodgroup: resCb.bloodgroup ? resCb.bloodgroup : "",
      city: resCb.city ? resCb.city : "",
      lname: resCb.lname ? resCb.lname : "",
      mname: resCb.mname ? resCb.mname : "",
      occupation: resCb.occupation ? resCb.occupation : "",
      patientcode: resCb.patientcode ? resCb.patientcode : "",
      pincode: resCb.pincode ? resCb.pincode : "",
      dob: resCb.dob ? resCb.dob : "",
      gender: resCb.gender ? resCb.gender : "",
      state: resCb.state ? resCb.state : "",
      email: resCb.email ? resCb.email : "",
      fathern: resCb.fathern ? resCb.fathern : "",
      mothern: resCb.mothern ? resCb.mothern : "",
      fathernorspouse: resCb.fathernorspouse ? resCb.fathernorspouse : "",
      admsnDate:
        resCb.visit && resCb.visit.admsnDate
          ? (() => {
            const raw = resCb.visit.admsnDate;
            const formatted = format(
              utcToZonedTime(new Date(raw), "Asia/Kolkata"),
              "MMM dd yyyy HH:mm"
            );
            console.log("[PatientSections] admission date from API (getProfile): raw=", raw, "| displayed (IST)=", formatted);
            return formatted;
          })()
          : "",
      dischargeDate: resCb.visit?.dischargeDate
        ? format(
          utcToZonedTime(new Date(resCb.visit.dischargeDate), "Asia/Kolkata"),
          "MMM dd yyyy HH:mm"
        )
        : "",
      type: resCb.visit ? resCb.visit.type : "InPatient",
      department: resCb.visit ? resCb.visit.department : "",
      insurance:
        resCb.visit && resCb.visit.insurance ? resCb.visit.insurance : "NO",
      fileNo: resCb.fileNo ? resCb.fileNo : "",
      education: resCb.education ? resCb.education : "",
      srcRef: resCb.srcRef ? resCb.srcRef : "",
      language: resCb.language ? resCb.language : "",
      sociostatus: resCb.sociostatus ? resCb.sociostatus : "",
      tokenNumber: resCb.tokenNumber ? resCb.tokenNumber : "",
      deskNumber: resCb.deskNo ? resCb.deskNo : "",
      comments: resCb.comments ? resCb.comments : "",
      status: resCb.visit && resCb.visit.status ? resCb.visit.status : "",
      ehrid: resCb.ehrid ? resCb.ehrid : "",
      erpCustId: resCb.erpCustId ? resCb.erpCustId : "",
      visitDate: resCb.visit ? resCb.visit.visitDate : "",
      registeredOn: resCb.registeredOn ? resCb.registeredOn : "",
      internalStatus: resCb.visit ? resCb.visit.internalStatus : "",
      visitflag: resCb.visit ? resCb.visit.visitflag : "",
      emrPhono: resCb.emrPhono ? resCb.emrPhono : "",
      custGroupId: resCb.custGroupId ? resCb.custGroupId : "",
      regFeesPdfUrl:
        resCb.visit && resCb.visit.regFeesPdfUrl
          ? resCb.visit.regFeesPdfUrl
          : "",
      dob: resCb.dob ? resCb.dob : "",
      ipopnumber: resCb.visit?.ipopnumber || "",
      visit: {
        ...resCb.visit,
        reference: resCb.visit?.reference ?? { refby: "", refkey: "" },
        referenceId: resCb.visit?.referenceId ?? "",
      },
    };
    setRegDate(
      resCb.registeredOn
        ? format(new Date(resCb.registeredOn), "MMM dd yyyy")
        : ""
    );
    console.log("pateintInfo in patientsection: ", patientInfo);
    console.log("person in patientsection: ", personData);
    setPatientInfo((previousPatientInfo) => ({
      ...previousPatientInfo,
      ...personData,
    }));
    getAssignedRoom(personData);
    console.log("pateintInfo in patientprofile : ", patientInfo);
  };

  const getVisitList = (patId, vId) => {
    let data = {};
    data["visitId"] = vId;
    data["patientId"] = patId;
    let type = "showAllVisits";
    dispatch(
      patientActions.getVisitListById(vId, type, (resCb) => {
        if (resCb && isMountedRef.current) {
          // 🔄 Update Redux patientVisitLists so SearchDocList component
          // (which reads from state.patientDetailsState.patientVisitLists)
          // gets the updated doctor/room assignments after a booking plan update
          const visitListData = resCb?.patsearchreslt || [];
          if (visitListData.length > 0) {
            dispatch(patientActions.setPatientVisitList(visitListData));
            console.log("[PatientSections] Dispatched setPatientVisitList with updated visit data for doctor/sync reflection in SearchDocList");
          }

          const firstVisit = visitListData[0];

          if (firstVisit) {
            // ✅ Get current visit's doctor and counselor names for printing
            const currentDoctorNameForPrint = firstVisit?.person
              ? `${firstVisit.person.displaytitle || ""} ${firstVisit.person.first_name || ""} ${firstVisit.person.last_name || ""}`.trim()
              : "—";

            const currentCounselorNameForPrint = firstVisit?.counsellor
              ? `${firstVisit.counsellor.displaytitle || ""} ${firstVisit.counsellor.first_name || ""} ${firstVisit.counsellor.last_name || ""}`.trim()
              : "—";

            // Store in state for use in print component
            setCurrentDoctorName(currentDoctorNameForPrint);
            setCurrentCounselorName(currentCounselorNameForPrint);

            console.log("Current Doctor for visit", vId, ":", currentDoctorNameForPrint);
            console.log("Current Counselor for visit", vId, ":", currentCounselorNameForPrint);

            setPatientRegInfo(firstVisit);
            if (firstVisit.visit?.invoicePdf) {
              setRegisterPdfname(firstVisit.visit.invoicePdf);
            }

            console.log("visitdate is", firstVisit.visit?.visitDate);
            console.log("person object in visitlist:", firstVisit.person);

            const docinfo = {
              counselor: firstVisit.person?.personId || "",
              email: firstVisit.person?.email || "",
              gvId: firstVisit.person?.gvId || "",
              type: "edit",
            };

            if (resCb.patsearchreslt[0].person) {
              console.log(
                "patient object in visitlist :",
                resCb.patsearchreslt[0]
              );
              docinfo.counselor = resCb.patsearchreslt[0].person.personId;
              docinfo.email = resCb.patsearchreslt[0].person.email;
              docinfo.gvId = resCb.patsearchreslt[0].person.gvId;
              getCalenderForPatient(docinfo.gvId, patId.toString(), "doctor");
            }

            setdocInfo(docinfo);

            const counsInfo = {
              counselor: firstVisit.counsellor?.personId || "",
              email: firstVisit.counsellor?.email || "",
              gvId: firstVisit.counsellor?.gvId || "",
              type: "edit",
            };

            if (resCb.patsearchreslt[0].counsellor) {
              counsInfo.counselor = resCb.patsearchreslt[0].counsellor.personId;
              counsInfo.email = resCb.patsearchreslt[0].counsellor.email;
              counsInfo.gvId = resCb.patsearchreslt[0].counsellor.gvId;
              getCalenderForPatient(
                counsInfo.gvId,
                patId.toString(),
                "counsellor"
              );
            }

            setcounsInfo(counsInfo);

            // ✅ FIX: Update lastAsssigned from the CURRENT visit's data.
            // getPatientProfile sets lastAsssigned from the *previous* visit (lastvisitId = visitList[1]),
            // so after a booking save it would show the old doctor. We override it here with
            // the freshly-fetched current visit assignment so the "Last Assigned" panel is always up-to-date.
            const currentVisitAssignments = firstVisit?.visitAssignmentList || [];
            const personLookupCurrent = {};
            if (resCb.personList) {
              resCb.personList.forEach((p) => {
                personLookupCurrent[p.personId] = p;
              });
            }

            // Also try to look up doctor from personDetailsService cache as fallback
            const doctorsDetail = personDetailsService.getDoctorsDetail();

            const updatedLastAssigned = { room: "" };

            // Prefer Primary Doctor from visitAssignmentList (most authoritative)
            // Also include groupname="new session" (saved by BookingNewSession)
            const latestCurrentDoctor = [...currentVisitAssignments]
              .reverse()
              .find(
                (a) =>
                  (a.groupname == null ||
                    a.groupname === "Primary Doctor" ||
                    a.groupname === "new session") &&
                  a.targetid &&
                  a.status === "open"
              );

            if (latestCurrentDoctor) {
              // First try personList from response
              let p = personLookupCurrent[latestCurrentDoctor.targetid];
              
              // If not found, try doctors detail cache (more comprehensive)
              if (!p && Array.isArray(doctorsDetail)) {
                p = doctorsDetail.find(doc => 
                  doc && (doc.personId == latestCurrentDoctor.targetid || 
                  doc.gvId == latestCurrentDoctor.targetid || 
                  doc.gvid == latestCurrentDoctor.targetid)
                );
                // Normalize property names if found in doctorsDetail
                if (p) {
                  p = {
                    personId: p.personId || p.gvId || p.gvid,
                    gvId: p.gvId || p.gvid || p.personId,
                    first_name: p.first_name || p.firstName || '',
                    last_name: p.last_name || p.lastName || '',
                    displaytitle: p.displaytitle || p.title || '',
                  };
                }
              }
              
              if (p) {
                updatedLastAssigned.doctor =
                  `${p.displaytitle || ""}.${p.first_name || ""} ${p.last_name || ""}`.trim();
                updatedLastAssigned.doctorId = p.personId || p.gvId;
              } else {
                // Even if we can't resolve the name, set it using targetid so it's not blanked
                updatedLastAssigned.doctor = `Doctor ID: ${latestCurrentDoctor.targetid}`;
                updatedLastAssigned.doctorId = latestCurrentDoctor.targetid;
              }
            } else if (firstVisit.person) {
              // Fallback to person field on firstVisit
              updatedLastAssigned.doctor =
                `${firstVisit.person.displaytitle || ""}.${firstVisit.person.first_name || ""} ${firstVisit.person.last_name || ""}`.trim();
              updatedLastAssigned.doctorId =
                firstVisit.person.personId || firstVisit.person.gvId;
            }
            
            const latestCurrentRoom = [...currentVisitAssignments]
              .reverse()
              .find(
                (a) =>
                  ((a.groupname || "").toLowerCase() === "room" || (a.type || "").toLowerCase() === "room") &&
                  (a.targetid || a.targetname)
              );

            if (latestCurrentRoom) {
              updatedLastAssigned.room =
                latestCurrentRoom.targetname ||
                (latestCurrentRoom.targetid ? `Room ${latestCurrentRoom.targetid}` : "");
              updatedLastAssigned.roomId = latestCurrentRoom.targetid;
            } else if (firstVisit.room) {
              updatedLastAssigned.room =
                firstVisit.room.roomName ||
                firstVisit.room.name ||
                firstVisit.room.label ||
                (firstVisit.room.id ? `Room ${firstVisit.room.id}` : "");
              updatedLastAssigned.roomId = firstVisit.room.id || firstVisit.room.roomId || firstVisit.room.roomid;
            }
//ft
            // Prefer Primary Counsellor from visitAssignmentList
            const latestCurrentCounsellor = [...currentVisitAssignments]
              .reverse()
              .find(
                (a) =>
                  a.groupname === "Primary Counsellor" &&
                  a.targetid &&
                  a.status === "open"
              );

            if (latestCurrentCounsellor) {
              const p = personLookupCurrent[latestCurrentCounsellor.targetid];
              if (p) {
                updatedLastAssigned.counsellor =
                  `${p.displaytitle || ""}.${p.first_name || ""} ${p.last_name || ""}`.trim();
                updatedLastAssigned.counsellorId = p.personId || p.gvId;
              }
            } else if (firstVisit.counsellor) {
              // Fallback to counsellor field on firstVisit
              updatedLastAssigned.counsellor =
                `${firstVisit.counsellor.displaytitle || ""}.${firstVisit.counsellor.first_name || ""} ${firstVisit.counsellor.last_name || ""}`.trim();
              updatedLastAssigned.counsellorId =
                firstVisit.counsellor.personId || firstVisit.counsellor.gvId;
            }

            // Only override if we actually resolved some data (avoid blanking if response is partial)
            if (updatedLastAssigned.doctor || updatedLastAssigned.counsellor || updatedLastAssigned.room) {
              setLastAssigned({ ...updatedLastAssigned });
              console.log("[getVisitList] Updated lastAsssigned from current visit:", updatedLastAssigned);
            }
          }

          setDeskDetails(resCb.patientVisitMap);
          setPatientVisitList(resCb.patientVisitMap);
          dispatch(patientActions.setPatientVisitList(resCb.patsearchreslt));
          setLoader(false);
        }
      })
    );
  };

  const toProperCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getFullName = (fname = "", mname = "", lname = "") => {
    return [fname, mname, lname].filter(Boolean).join(" ");
  };

  const toggleBasicInfo = () => {
    setCollapseBasicInfo(!collapseBasicInfo);
  };

  const toggleAttachments = () => {
    setCollapseAttachment(!collapseAttachment);
  };
  const toggleComplaints = () => {
    setCollapseComplaints(!collapseComplaints);
  };
  const toggleDoctorList = () => {
    setCollapseDoctorList(!collapseDoctorList);
  };
  const toggleReference = () => {
    setCollapseReference(!collapseReference);
  };
  const toggleBarcode = () => {
    setCollapseBarcode(!collapseBarcode);
  };
  const toggleRoom = () => {
    setCollapseRoom(!collapseRoom);
  };
  const toggleInsurance = () => {
    setCollapseInsurance(!collapseInsurance);
  };
  const diagnosisClick = () => {
    if (dPatient) {
      this.props.history.push({
        pathname: `/DiagnosisMaster/${dPatient.basicinfo.patientId}`,
        state: {
          patientId: dPatient.basicinfo.patientId,
        },
      });
    }
  };

  const handleDocChange = (event) => {
    console.log("event:", event);
    var ServerResVal = ServerRes;
    var assigndoc = {};
    assigndoc["id"] = patientInfo.id;
    assigndoc["visit"] = {};
    assigndoc.visit["id"] = patientInfo.visitId;
    assigndoc.visit["patientId"] = patientInfo.patientId;

    console.log(" assign doc : ", patientInfo);
    if (event != null) {
      let person = {
        personId: event.gvId,
        first_name: event.firstName,
        last_name: event.lastName,
        department: event.department,
        departmentId: event.departmentId,
        role: event.role,
        type: event.type,
      };
      assigndoc["person"] = person;
      // visitdetail.counselor=event;
      console.log("event !=null : ", event);
    }
    console.log("D-appointmentdetails", assigndoc);
    dispatch(
      doctorActions.assignDoctor(assigndoc, (resCb) => {
        if (resCb) {
          console.log("Doctor assigned successfully:", resCb);
          if (resCb.state === "success") {
            ServerResVal.success = "Doctor assigned successfully";
            getDoctorList();
            setServerRes({ ...ServerResVal });
            let info = {
              counselor: resCb.va.targetid,
              email: "",
              type: "edit",
              gvId: resCb.va.targetid,
            };

            setdocInfo({ ...info });
            console.log("visit id is : ", patientInfo.visitId);
            console.log("gv id is : ", event.gvId);
            console.log("token number is: ", patientInfo.tokenNumber);
            if (
              patientInfo &&
              patientInfo.type &&
              patientInfo.type == "OutPatient"
            ) {
              console.log(
                "patientInfo.tokenNumber",
                patientInfo.tokenNumber,
                typeof patientInfo.tokenNumber
              );
              if (patientInfo && parseInt(patientInfo.tokenNumber) > 0) {
                dispatch(
                  tokenActions.recorrectToken(
                    patientInfo.visitId,
                    event.gvId,
                    (resCb) => {
                      if (resCb) {
                        console.log("token responses", resCb.token);
                      }
                    }
                  )
                );
              } else {
                dispatch(
                  tokenActions.tokenGenerate(
                    patientInfo.visitId,
                    event.gvId,
                    (resCb) => {
                      if (resCb) {
                        console.log("token responses", resCb.token);
                      }
                    }
                  )
                );
              }
            }
          } else if (resCb.state === "error") {
            ServerResVal.error = "Failed to assign doctor";
            setServerRes({ ...ServerResVal });
          }

          setTimeout(() => {
            if (isMountedRef.current) {
              closeAlert();
            }
          }, 2000);
          setApiCall(true);
          // Refresh patient data after doctor assignment
          if (patientInfo && patientInfo.id && patientInfo.visitId) {
            // Explicitly refresh visit list to update lastAsssigned doctor info
            getVisitList(patientInfo.id, patientInfo.visitId);
            getProcedureList(patientInfo.id, patientInfo.visitId);
            getAssignedAllRoomlist(patientInfo.visitId);
          }
          setupdateTokenDashboard(true);
          console.log("ServerResVal", ServerResVal);
        }
      })
    );
  };

  const handleCounslChange = (event) => {
    console.log("handleCounslChange  : ", event);
    console.log("patientInfo  : ", patientInfo);

    // return;
    let details = { ...summary };

    details.shortdesc =
      "Added Counsellor " +
      event.displaytitle +
      " " +
      event.firstName +
      " " +
      event.lastName;
    details.groupname = "Counsellor";
    details.longdesc = "Assigned Counsellor";
    details.visitId = patientInfo.visitId;
    details.patientId = patientInfo.id;
    details.targetid = event.gvId;
    var ServerResVal = ServerRes;

    console.log("details to be sent in patientsection component:", details);

    let flag = "ReAssignCounsellor";
    dispatch(
      doctorActions.addConsultant(details, "Counsellor", flag, (resCb) => {
        if (resCb) {
          if (resCb.successMessages) {
            ServerResVal.success = "Counsellor assigned successfully.";

            // props.onModalClose(ServerResVal);
            getDoctorList();
            setServerRes({ ...ServerResVal });
            let info = {
              counselor: resCb.va.targetid,
              email: "",
              type: "edit",
              gvId: resCb.va.targetid,
            };

            setcounsInfo({ ...info });
            console.log("counsellor updated:", info);
          } else {
            ServerResVal.error = "Failed to Assign Counsellor.";
          }
          setServerRes({ ...ServerResVal });
          setTimeout(() => {
            if (isMountedRef.current) {
              closeAlert();
            }
          }, 2000);
          console.log("resCbe : ", resCb);
        }
      })
    );
  };

  // }

  const handleBackClick = () => {
    history.push("/Home");
  };
  const closeModal = (event) => {
    setuploadPreview(!setuploadPreview);
    setCollapseAttachment(!collapseAttachment);
    console.log(" upload prevew closed ");
    //dispatch illige
    //  props.setState({flieUploadErrorMsg:''});
    //  props.setState({flieUploadMsg:''});
  };
  const closeMessageStrip = () => {
    console.log("closing msg strip fn in patientlist");
    setShowMsgStrip(!showMsgStrip);
  };

  const openAttachment = (event) => {
    setuploadPreview(!setuploadPreview);
    // props.closeAttachAction(false)
    dispatch(patientActions.setUploadedFiles(""));
  };

  const searchResult = (event) => {
    let searchvalue = JSON.parse(sessionStorage.getItem("searchvalue"));
    console.log("patientsearchstate", props);
    history.push({
      pathname: `/Home`,
      //   state: {
      //    patientsearch: props.patientsearchlist,
      //  },
    });
  };

  const updatePatientDetails = (event, payment) => {
    var ServerResVal = ServerRes;
    console.log("patient added successfully:", event);
    console.log("payemnt :", payment);
    if (event) {
      if (event.successMessages) {
        setServerRes({ ...ServerResVal });
        setMessageStrip({ success: event.successMessages[0] });
        setTimeout(() => {
          console.log("props:", props);
          let patientId;
          let visitId;
          if (event.patientresp.visit) {
            console.log("here here ");
            patientId = event.patientresp.visit.patientId;
            visitId = event.patientresp.visit.id;
          } else {
            console.log("edit ifcase");
            patientId = event.patientresp.patientId;
            visitId = event.patientresp.visitId;
          }

          let erpCustId = event.patientresp.erpCustId;
          let name = event.patientresp.fname;
          // props.history.push({
          //   pathname: `/PatientSections/${patientId}/${visitId}`,
          // })
          if (payment === "payment") {
            props.history.push({
              pathname: `/Payment/${name}/${patientId}/${visitId}/${erpCustId}`,
            });
          }
          if (payment === "dependent") {
            props.history.push({
              pathname: `/PatientSections/${patientId}/${visitId}`,
            });
          }

          if (patientId && patientId != "" && visitId && visitId != "") {
            console.log("patient rech patient:.");
            getAssignedAllRoomlist(visitId);
            console.log("patient rech patient:.", patientId, visitId);
            getPatientProfile(patientId, visitId);
            getProcedureList(patientId, visitId);
            getVisitList(patientId, visitId);
            //setCallType("Add");
            //setPatientInfo({patientId:patId,visitId:vId })
          }
        }, 3000);
      } else {
        // setMessageStrip({ error: event.errorMessages[0] });
      }
    }
  };

  const openOnboardModal = (patientdetails) => {
    //props.openOnboardModal(patientdetails);
    setOnBoardModal(true);
  };
  const handlevisitSelect = (e) => {
    const visitIdValue = e.target.value;
    setSelecetdDate(visitIdValue);
    setSelecetdDateVisit(visitIdValue);
    setSelectedVisitId(visitIdValue);
  };
  const handleSelect = () => {
    const selectedId = selectedVisitId || selectedVisit || selectedDate;
    console.log("handleSelect:", selectedId);
    if (selectedId) {
      handleChange({ target: { value: selectedId } });
    }
  };

  const handleChange = (event, value) => {
    //appliedFilters.date = event.target.value ? true : false;
    //selectedDate = event.target.value ;
    console.log("event:", event);

    if (event != null && event.target && event.target.value) {
      let patId = props.match.params.patientId;
      let vId = event.target.value;

      console.log("event.target.value:", vId);
      setSelecetdDate(vId);
      setSelecetdDateVisit(vId);
      setSelectedVisitId(vId);
      if (vId === "Select Visit Date") return;
      props.history.push({
        pathname: `/PatientSections/${patId}/${vId}`,
      });
      getProcedureList(patId, vId);
      getVisitList(patId, vId);
      getAssignedAllRoomlist(vId);
      getPatientSessionDetails(patId, vId);
    }
    setViewVisit(false);
  };

  const closeAlert = (e) => {
    setServerRes({ ..."" });
  };

  const updateInsuranceProcedure = (e) => {
    setInsuranceDesk(false);
    if (e) {
      setMessageStrip(e);
    }
  };
  const barcodeId = (number, width) => {
    console.log("barcodeId", typeof number);
    // const numberString = String(number);
    // const numberOfZeros = width - number.length;
    // if (numberOfZeros <= 0) {
    //   return numberString;
    // }
    // const paddedZeros = Array(numberOfZeros).fill('0').join('');
    // return paddedZeros + numberString;
    const numberString = number.toString();
    const zerosToAdd = Math.max(0, width - numberString.length);
    return "0".repeat(zerosToAdd) + numberString;
  };
  const setMessageStrip = (e) => {
    console.log("message obtained", e);
    var ServerResVal = ServerRes;
    if (e.success) {
      console.log("success message:", e.success);
      ServerResVal.success = e.success;
    } else {
      console.log("error message:", e.error);
      ServerResVal.error = e.error;
    }
    setServerRes({ ...ServerResVal });
    setTimeout(() => {
      if (isMountedRef.current) {
        closeAlert();
      }
    }, 5000);
  };
  const barcodeuac13 = barcodeId(props.match.params.visitId, 13);

  const str = patientInfo.fname + patientInfo.lname;
  const strlen = str.toString().slice(0, 8);
  console.log("canvas:", document.getElementById("mybarcode"));
    console.log("Vinayyyyyy");
  console.log("statussssssssssssssssssssssss:", patientInfo.status);
  const { inputRef } = useBarcode({
    value: barcodeuac13,
    options: {
      displayValue: true,
      background: "#F5F6F7",
    },
  });
  const openPayment = () => {
    // setShowPayment(true);
    console.log("patientInfo:", patientInfo);
    let patientId = patientInfo.id;
    let name = patientInfo.fname;
    let visitId = patientInfo.visitId;
    let erpCustId = patientInfo.erpCustId;
    props.history.push({
      pathname: `/Payment/${name}/${patientId}/${visitId}/${erpCustId}`,
    });
  };

  const closePayment = () => {
    setShowPayment(false);
  };

  //   const getPDF =()=>{
  //     // dispatch(patientActions.getRegistrationPDF( (resCb) => {
  //     //     if (resCb) {
  //     //     }
  //     //   })
  //     // );
  //   //  console.log("getpdf")
  // //  // let fs = require('fs');
  // //   var path = '/uploads/Registraion_Fees_Invoice/'+registerPdfname;

  // //   const docDefinition = {
  // //     content: [
  // //       {
  // //         image: path,
  // //         width: 500
  // //       }
  // //     ]
  // //   };// Opens the PDF in a new tab in the browser
  // // "C:/Users/prola/Hms/redicare/hosp_src/public/uploads/Registraion_Fees_Invoice"
  // //const imageFile = '/uploads/Registraion_Fees_Invoice/'+registerPdfname ;

  // // Create a new FileReader instance
  // //const reader = new FileReader()

  // // Set up the onload function to create a data URL for the image
  // // reader.onload = () => {
  // //   const imageDataUrl = reader.result // This will contain the data URL for the image
  // //   // ... Use the imageDataUrl for your purposes
  // // }

  // // Read the image file as a data URL
  // //reader.readAsDataURL(imageFile);
  // // const docDefinition = {
  // //       content: [
  // //         {
  // //           image: reader.readAsDataURL(imageFile),
  // //           width: 500
  // //         }
  // //       ]
  // //     }
  // //     PdfPrinter.createPdf(docDefinition).open();
  // // const fs = require('fs');
  // // let pdffjf = '/uploads/Registraion_Fees_Invoice/'+registerPdfname;
  // // console.log("pdffilenrjkfou",pdffjf)
  // // const pdfFile = fs.readFileSync('/uploads/Registraion_Fees_Invoice/'+registerPdfname);

  // // const pdfBuffer = Buffer.from(pdfFile);
  // // const docDefinition = {
  // //   content: [
  // //   ]
  // // };
  // // docDefinition.content.push({ image: pdfBuffer, width: 500 });
  // // const imageFile = '/uploads/Registraion_Fees_Invoice/'+registerPdfname ;
  // // PdfPrinter.createPdf(docDefinition).getDataUrl((dataUrl) => {
  // //   const win = window.open();
  // //   win.document.write('<iframe src="' + dataUrl + '" width="100%" height="100%" frameborder="0"></iframe>');
  // // });

  // //PdfPrinter.createPdf(docDefinition).open();
  //   }

  const fetchPdf = async () => {
    console.log("registeredpdf", registerPdfname);
    // const url = "/uploads/Registraion_Fees_Invoice/" + registerPdfname; // Replace with the URL of the PDF file on your server
    const url = REG_INVOICE + registerPdfname;
    fetch(url)
      .then((response) => response.blob())
      .then((pdfBlob) => {
        console.log("blobbb", pdfBlob);

        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      })
      .catch((error) => {
        console.error("Error fetching PDF file:", error);
      });
  };

  const regCardPdf = () => {
    setRegistrationCard(true);
  };
  const tokenPrint = () => {
    setPrintTOkenModal(true);
    setTimeout(() => {
      setPrintTOkenModal(false);
    }, 500);
  };

  const toggleonboard = (event) => {
    setOnBoardModal(false);
  };
  const onValueChange = (value) => {
    setSelectedRadio(value);
    console.log("selectedOptionRadio", value);
  };
  const onboardPatient = (patient) => {
  var ServerResVal = ServerRes;
  let type = "";
  let visitListDetails = patientProcedureList;
 
  if (selectedRadio == "Proxy") {
    type = "OutPatient";
    visitListDetails.visitflag = "Proxy";
  } else if (selectedRadio == "Walkin") {
    type = "InPatient";
    visitListDetails.visitflag = "Walkin";
  } else {
    type = selectedRadio;
	visitListDetails.visitflag = null;
  }
 
  console.log("type in log:", type);
  visitListDetails.type = type;
 
  const list = customerGroup || [];
  const matchedGroup = list.find(
    (group) => group.custGroupId == patientProcedureList.custGroupId
  );
  visitListDetails.custGrpDesc = matchedGroup?.description || "";
  console.log("visitlist details in onboard", visitListDetails);

  let admissionDateTime = "";
  if (type == "InPatient") {
    const dateTimeString = `${selecteddate} ${selectedTime}:00`;
    admissionDateTime = dateTimeString;
    console.log("timestamp", admissionDateTime);
  }
 
  visitListDetails.onboardStatus = "reVisitPatient";
  visitListDetails.appointmentFlag = false;
 
  dispatch(
    patientActions.onboardPatient(
      visitListDetails,
      type,
      admissionDateTime,
      (resCb) => {
        if (resCb) {
          console.log("onboardPatient response", resCb);
          if (resCb.successMessages) {
            let visitId = resCb.patientresp.visit.id;
            let patientId = resCb.patientresp.id;
            let name = resCb.patientresp.fname;
            let visitStatus = resCb.patientresp.visit.status;
            let erpCustId = resCb.patientresp.erpCustId;
           
            ServerResVal.success = "Patient onboarded successfully";
            setServerRes({ ...ServerResVal });
           
            setTimeout(() => {
              if (isMountedRef.current) {
                closeAlert();
              }
            }, 3000);
           
            setTimeout(() => {
              if (visitStatus && visitStatus == "open") {
                props.history.push({
                  pathname: `/PatientSections/${patientId}/${visitId}`,
                });
                setonBoardupdate(true);
              } else if (
                org?.orgCode === ORG_CODES.MNH ||
                org?.orgCode === ORG_CODES.VEDAMAAYU
              ) {
                // For both MNH and Vedamaayu, call registration fees API
                dispatch(
                  patientActions.registrationFees(
                    erpCustId,
                    0,
                    visitId,
                    patientId,
                    org?.orgCode,
                    [],
                    (resCbFees) => {
                      if (resCbFees && resCbFees.successMessages) {
                        console.log(
                          "resCb in registration fees for",
                          org?.orgCode,
                          ": ",
                          resCbFees
                        );
                        setTimeout(() => {
                          props.history.push({
                            pathname: `/PatientSections/${patientId}/${visitId}`,
                          });
                          getPatientProfile(patientId, visitId);
                          getProcedureList(patientId, visitId);
                          getVisitList(patientId, visitId);
                          closeAlert();
                        }, 2000);
                      }
                    }
                  )
                );
              } else {
                props.history.push({
                  pathname: `/Payment/${name}/${patientId}/${visitId}/${erpCustId}`,
                });
              }
            }, 2000);
          } else if (resCb.state === "error") {
            ServerResVal.error = "Patient onboard failed";
            setServerRes({ ...ServerResVal });
            setTimeout(() => {
              if (isMountedRef.current) {
                closeAlert();
              }
            }, 3000);
          }

          setOnBoardModal(false);
        }
      }
    )
  );
};

  const downloadBarcode = (event) => {
    //event.preventDefault();
    const canvas = document.getElementById("mybarcode");
    console.log("canvas", canvas);
    const dataURI = canvas.toDataURL();
    // console.log(dataURI)
    var arrayURI = [];
    for (var i = 0; i < 6; i++) {
      // arrayURI.push({image:dataURI,width:250,height:50,marginBottom:10})
      arrayURI.push({
        columns: [
          { image: dataURI, width: 250, height: 50, marginBottom: 10 },
          // { image: dataURI, width: 250, height: 50, marginBottom: 10 },
        ],
      });
    }
    setarrayui(arrayURI);
    console.log("URI", arrayURI);
    var docDefinition = {
      pageSize: "A4",
      pageMargins: [40, 55, 40, 55],
      pageOrientation: "portrait",
      defaultStyle: {
        fontSize: 12,
        columnGap: 20,
      },
      content: [arrayURI],
    };

    // PdfPrinter.createPdf(docDefinition).open();
    // pdfMake.createPdf(docDefinition).download()
  };

  const printAll = () => {
    console.log("printall clicked");
    // SetMergePdf(true);
    SetSeparatePdf(true);
    setPrintFiles(true);
    downloadBarcode();
    setVisitAction(false);
    setSurgeryConsentPrint(true);
    setPanchkarmaConsentPrint(true);
    setAdmissionConsentPrint(true);
    setPainAssessmentPrint(true);
  };

  const printPatientCard = () => {
    setPatientCardPrint(true);
  };

  const createAuthToken = () => {
    let data = {};
    if (
      patientInfo &&
      patientInfo.patientId != null &&
      patientInfo.visitId != null
    ) {
      data.patientId = patientInfo.patientId;
      data.visitId = patientInfo.visitId;
      dispatch(
        patientActions.uploadToken(data, (resCb) => {
          if (resCb && resCb.oAuthAccessToken) {
            console.log("response of upload token api ", resCb);
            let authToken = resCb.oAuthAccessToken;
            scanDocument(authToken);
          }
        })
      );
    } else {
      console.log("PatientId or visitId null");
    }
  };

  const checkScanner = () => {
    dispatch(
      patientActions.checkScanner((resCb) => {
        if (resCb) {
          console.log("response of checkScanner api ", resCb);
          if (resCb.printer && resCb.printer == 1) {
            createAuthToken();
          } else {
            console.log("No scanners available");
          }
        }
      })
    );
  };

  const openMembershipDialog = async () => {
    if (!isVedamaayu) return;

    setMembershipDialogOpen(true);
    setSelectedMembershipPlanId("");
    setMembershipReceiptNo("");
    setMembershipLoading(true);
    try {
      const plans = await subscriptionService.getSubscriptionPlans();
      setMembershipPlans(Array.isArray(plans) ? plans : []);
      if (!Array.isArray(plans) || plans.length === 0) {
        setMessageStrip({ error: "No membership plans are configured" });
      }
    } catch (error) {
      setMembershipPlans([]);
      setMessageStrip({
        error: error?.message || error || "Unable to load membership plans",
      });
    } finally {
      setMembershipLoading(false);
    }
  };

  const closeMembershipDialog = () => {
    if (membershipLoading) return;
    setMembershipDialogOpen(false);
  };

  const loadActiveMembershipStatus = async () => {
    if (!isVedamaayu) {
      if (isMountedRef.current) setHasActiveMembership(false);
      return;
    }

    const currentPatientId = patientId || props.match?.params?.patientId;
    if (!currentPatientId) return;
    try {
      const response = await subscriptionService.getActiveSubscriptionStatus(
        currentPatientId,
      );
      if (isMountedRef.current) {
        setHasActiveMembership(response?.active === true);
      }
    } catch (error) {
      if (isMountedRef.current) setHasActiveMembership(false);
    }
  };

  useEffect(() => {
    loadActiveMembershipStatus();
  }, [isVedamaayu, patientId, props.match?.params?.patientId]);

  const openMembershipServices = () => {
    if (!isVedamaayu) return;

    const currentPatientId = patientInfo.id || patientId;
    const currentVisitId = patientInfo.visitId || visitId;
    history.push(`/billing/${currentPatientId}/${currentVisitId}?tab=membership`);
  };

  const startMembership = async () => {
    if (!isVedamaayu) return;

    if (!selectedMembershipPlanId) {
      setMessageStrip({ error: "Please select a membership plan" });
      return;
    }
    if (!membershipReceiptNo.trim()) {
      setMessageStrip({ error: "Please enter the invoice number" });
      return;
    }

    setMembershipLoading(true);
    try {
      const response = await subscriptionService.activateSubscription({
        patientId: Number(patientId || props.match?.params?.patientId),
        planId: Number(selectedMembershipPlanId),
        receiptNo: membershipReceiptNo.trim().toUpperCase(),
      });
      setMessageStrip({
        success: `${response?.planName || "Membership"} started successfully until ${
          response?.endDate || "the configured end date"
        }`,
      });
      setHasActiveMembership(true);
      setMembershipDialogOpen(false);
    } catch (error) {
      setMessageStrip({
        error: error?.message || error || "Unable to start membership",
      });
    } finally {
      setMembershipLoading(false);
    }
  };

  const scanDocument = (authToken) => {
    dispatch(
      patientActions.scanDocument(authToken, (resCb) => {
        if (resCb) {
          console.log("response of scanDocument api ", resCb);
        }
      })
    );
  };

  const modalPopUp = () => {
    setModalopen(true);
    setAnchorEl(null);
  };
  const [modalopen, setModalopen] = useState(false);

  const convertToInPatient = () => {
    var ServerResVal = ServerRes;
    patientDetail.patientId = patientInfo.id;
    patientDetail.visitId = parseInt(patientInfo.visitId);
    patientDetail.closecomment = "CONVERTTOINPATIENT";
    console.log("Convert to Inpatient: start");
    dispatch(
      patientActions.updateVAStatus(patientDetail, (resCb) => {
        console.log("response close button:", resCb);
        if (resCb.respStatus == 0) {
          let type = "InPatient";
          console.log("type in log:", type);
          let visitListDetails = patientProcedureList;
          visitListDetails.onboardStatus = "CONVERTOUTTOINPATIENT";
          visitListDetails.type = type;
          const list = customerGroup || [];
          const matchedGroup = list.find(
            (group) => group.custGroupId == patientProcedureList.custGroupId
          );
          visitListDetails.custGrpDesc = matchedGroup?.description || "";
          console.log("Rescb", visitListDetails);

          console.log("visitlist details in onboard", visitListDetails);
          let admissionDateTime = "";
          const dateTimeString = `${selecteddate} ${selectedTime}:00`;
          admissionDateTime = dateTimeString;
          console.log("timestamp", admissionDateTime);
          visitListDetails.appointmentFlag = false;
          // console.log("log in onboard method:", value);
          dispatch(
            patientActions.onboardPatient(
              visitListDetails,
              type,
              admissionDateTime,
              (resCb) => {
                if (resCb) {
                  if (resCb.successMessages) {
                    ServerResVal.success =
                      "Outpatient converted to inpatient successfully";
                    setServerRes({ ...ServerResVal });
                  }
                  console.log("onboardPatient response", resCb);
                  let pateintId = resCb.patientresp.id;
                  let visitId = resCb.patientresp.visit.id;
                  setTimeout(() => {
                   // setProfileLoading(true);   
                    props.history.push({
                      pathname: `/PatientSections/${pateintId}/${visitId}`,
                    });
                    getPatientProfile(pateintId, visitId);
                    getProcedureList(pateintId, visitId);
                    getVisitList(pateintId, visitId);
                    closeAlert();
                  }, 2000);
                  // setPatientCloseModal(false);
                  // modalopen
                  // handleClose();
                }
              }
            )
          );
        }
      })
    );
    //setModalopen(false);
    // var ServerResVal = ServerRes;
    // console.log("convert to inpatient button clicked");
    // patientInfo.type = "InPatient";
    // let convert = "convertToInPatient";

    // let admissionDateTime = "";
    // const dateTimeString = `${selecteddate} ${selectedTime}:00`;
    // admissionDateTime = dateTimeString;
    // console.log("timestamp", admissionDateTime);

    // dispatch(
    //   patientActions.updatePatient(
    //     patientInfo,
    //     convert,
    //     admissionDateTime,
    //     (resCb) => {
    //       if (resCb) {
    //         console.log("Rescb", resCb.state, resCb.message);
    //         if (resCb.successMessages) {
    //           console.log("success message");
    //           ServerResVal.success =
    //             "Converted OutPatient to Inpatient successfully.";
    //           patientInfo.admsnDate = format(
    //             new Date(admissionDateTime),
    //             "MMM dd yyyy HH:mm"
    //           );
    //         } else if (resCb.state === "error") {
    //           ServerResVal.error = "Failed to convert OutPatient to Inpatient.";
    //         }
    //         setServerRes({ ...ServerResVal });
    //         setTimeout(() => {
    //           closeAlert();
    //         }, 3000);
    setModalopen(false);
    //       }
    //     }
    //   )
    // );
  };
  const toggle = (event) => {
    setModalopen(false);
  };
  const toggleVisitModal = () => {
    setViewVisit(false);
  };

  const [visitAction, setVisitAction] = useState(false);
  const [printAction, setPrintAction] = useState(false);
  const visitActions = (e) => {
    setVisitAction(true);
  };

  const printActions = (e) => {
    setPrintAction(true);
  };
  const closeDropDown = (e) => {
    if (visitAction) {
      setVisitAction(false);
    }
    if (printAction) {
      setPrintAction(false);
    }
  };

  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [schduleApp, setSchduleApp] = useState(false);
  const [viewCmplaint, setViewCmplaint] = useState(false);
  const [newCmplaint, setNewCmplaint] = useState(false);
  const [assignRoom, setAssignRoom] = useState(false);
  const [assignedRoomDet, setAssignedRoomDet] = useState();
  const [assignedAllRoomDet, setAssignedAllRoomDet] = useState([]);
  const [patientSessionsList, setPatientSessionsList] = useState([]);
  const [insuranceDesk, setInsuranceDesk] = useState(false);
  const [voucher, setVoucher] = useState(false);
  const [viewVisit, setViewVisit] = useState(false);
  const [addedependent, setAddDependent] = useState(false);

  // Reusable room item renderer
  const renderRoomItem = (room, roomType) => {
    const hasCheckout = Boolean(room?.roomlog?.coutdt);
    const roomName = room?.roomName || room?.name || room?.label || room?.targetname || room?.targetName || room?.room?.roomName || "Room";
    const floorId = room?.floorId != null ? room.floorId : room?.room?.floorId;

    let shortDisplay, fullDetails;

    if (roomType === 'stay') {
      shortDisplay = room?.roomlog?.cindt
        ? `${roomName}\nFrom: ${formatDateTime(room.roomlog.cindt)}`
        : roomName;
      fullDetails = room?.roomlog?.cindt
        ? `${roomName}, From: ${formatDateTime(room.roomlog.cindt)}, ${room.roomlog.coutdt
            ? `To: ${formatDateTime(room.roomlog.coutdt)}`
            : ""
          }`
        : roomName;
    } else {
      shortDisplay = room?.roomlog?.cindt
        ? `${roomName}\n${format(new Date(room.roomlog.cindt), "MMM dd yyyy")}\n${format(new Date(room.roomlog.cindt), "h:mm a")} - ${room.roomlog.coutdt ? format(new Date(room.roomlog.coutdt), "h:mm a") : ""}`
        : roomName;
      fullDetails = room?.roomlog?.cindt
        ? `${roomName}, ${format(new Date(room.roomlog.cindt), "MMM dd yyyy")}, ${format(new Date(room.roomlog.cindt), "h:mm a")} - ${room.roomlog.coutdt ? format(new Date(room.roomlog.coutdt), "h:mm a") : ""}`
        : roomName;
    }

    return (
      <p
        key={`${roomType}-${room.id || room.targetid || roomName}-${room.roomlog?.cindt || "current"}`}
        title={fullDetails}
        className={`my-2 ${hasCheckout ? "text-muted" : ""}`}
        style={{
          color: hasCheckout ? "#000000" : "#0b4623",
          fontSize: hasCheckout ? "" : "13px",
          fontWeight: hasCheckout ? "" : "500",
          whiteSpace: "pre-line",
        }}
      >
        {shortDisplay}
      </p>
    );
  };

  // Compute Stay Rooms and Session Rooms from assignedAllRoomDet
  const { stayRooms, sessionRooms } = useMemo(() => {
    return assignedAllRoomDet.reduce((acc, room) => {
	  // Older API payloads did not always serialize sessionId. Booking metadata
	  // is also authoritative and prevents a session room appearing as a stay.
      const sessionId = room?.roomlog?.sessionId ?? room?.roomStatus?.sessionId
		?? room?.sessionId ?? room?.session_id;
	  const isSessionRoom = sessionId != null || Boolean(
		room?.bookingCode || room?.bookingStatus || room?.sessionStatus
		|| room?.treatmentName || room?.packageService
	  );
      if (!isSessionRoom) {
        acc.stayRooms.push(room);
      } else {
        acc.sessionRooms.push(room);
      }
      return acc;
    }, { stayRooms: [], sessionRooms: [] });
  }, [assignedAllRoomDet]);

  const lastAssignedIsSessionRoom = useMemo(() => {
    if (!lastAsssigned?.roomId) return false;
    return sessionRooms.some((room) => {
      const roomId = room?.id ?? room?.roomId ?? room?.roomid
        ?? room?.roomStatus?.roomid ?? room?.roomStatus?.roomId;
      return roomId != null && String(roomId) === String(lastAsssigned.roomId);
    });
  }, [lastAsssigned?.roomId, sessionRooms]);

  // ── Session Room Details card formatting helpers ────────────────────────
  const formatSessionDate = (value) => {
    if (!value) return "-";
    try {
      return format(new Date(value), "dd MMM yyyy");
    } catch (e) {
      return "-";
    }
  };

  // fromTimestamp=true  -> value is a full ISO datetime (e.g. roomlog.cindt)
  // fromTimestamp=false -> value is a bare "HH:mm:ss" time string (e.g. sessionStartTime)
  const formatSessionTime = (value, fromTimestamp = false) => {
    if (!value) return "-";
    try {
      const dateObj = fromTimestamp ? new Date(value) : new Date(`1970-01-01T${value}`);
      return format(dateObj, "h:mm a");
    } catch (e) {
      return "-";
    }
  };

  // ── Navigate to Add Session (new booking plan) ──────────────────────────
  const handleAddSession = () => {
    const pId = patientInfo.id || patientInfo.patientId;
    const vId = patientInfo.visitId;
    if (!pId || !vId) return;

    const patientName = patientInfo.fullName || patientInfo.fname || 'Patient';
    const navigationState = {
      selectedPatient: {
        id: pId,
        patient_name: patientName,
        fname: patientName,
        phone: patientInfo.phno || '',
      },
      from: 'patientAppointment',
      returnTo: `/PatientSections/${pId}/${vId}`,
    };
    history.push({
      pathname: `/PatientSections/${pId}/${vId}/new-booking`,
      state: navigationState,
    });
  };

  // Editing a booking session is only allowed until 10 PM on the day it was
  // created/booked (session.created_at) — after that the edit option is locked.
  // Mirrors isEditWindowOpen in BookingPlans.js.
  const isEditWindowOpen = (session) => {
    if (!session?.created_at) return true;
    const createdAt = new Date(String(session.created_at).replace(' ', 'T'));
    if (isNaN(createdAt.getTime())) return true;
    const cutoff = new Date(createdAt);
    cutoff.setHours(22, 0, 0, 0);
    return new Date() <= cutoff;
  };

  // ── Navigate to Edit Session ────────────────────────────────────────────
  const handleEditSession = (room) => {
    const pId = patientInfo.id || patientInfo.patientId;
    const vId = patientInfo.visitId;
    if (!pId || !vId) return;

    // Extract the real booking_plans id from the room object
    const bookingId = room?.bookingId || room?.booking_id || room?.roomlog?.bookingId;
    if (!bookingId) {
      console.error('[PatientSections] Cannot edit session: missing bookingId', room);
      toast.error('Cannot edit session: missing booking ID');
      return;
    }

    // The booking id lives directly in the URL (route now captures :id), so
    // BookingNewSession always fetches the complete booking_plans record
    // (package, session, notes, medicine oil, etc.) via getBookingPlanById
    // instead of relying on a hand-built partial object passed through state.
    history.push({
      pathname: `/PatientSections/${pId}/${vId}/new-booking/${bookingId}`,
      state: {
        from: 'patientAppointment',
        returnTo: `/PatientSections/${pId}/${vId}`,
        existingVisitId: vId,
      }
    });
  };

  const getStatusBadgeStyle = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "completed") return { backgroundColor: "#e6f4ea", color: "#1e7e34" };
    if (normalized === "cancelled") return { backgroundColor: "#fdeaea", color: "#c62828" };
    if (normalized === "modified") return { backgroundColor: "#fff4e5", color: "#b26a00" };
    if (normalized === "scheduled" || normalized === "boarded") return { backgroundColor: "#e8f0fe", color: "#1a56db" };
    return { backgroundColor: "#f1f1f1", color: "#555" };
  };

  const editPatientProfile = () => {
    setOpenPatientModal(!openPatientModal);
  };
  const closePatientForm = () => {
    setOpenPatientModal(false);
  };
  const scheduleAppointment = () => {
    setSchduleApp(!schduleApp);
  };
  const closeAppointmentModal = () => {
    setSchduleApp(false);
  };
  const viewComplaints = (e) => {
    setViewCmplaint(!viewCmplaint);
  };
  const newComplaints = (e) => {
    setNewCmplaint(!newCmplaint);
    setAnchorEl(null);
  };
  const assignRooms = (e) => {
    if (isWalkinVisit) return;
    setAssignRoom(true);
    setAnchorEl(null);
  };

  const editdepndents = (e) => {
    setAddDependent(!addedependent);
    setAnchorEl(null);
  };

  const insuranceDeskDetails = (e) => {
    setInsuranceDesk(true);
    setAnchorEl(null);
  };
  const voucherInfo = (e) => {
    setVoucher(true);
    setAnchorEl(null);
  };
  const viewLastVisit = (e) => {
    setViewVisit(true);
    setAnchorEl(null);
  };

  const billingInfo = (e) => {
    setAnchorEl(null);
    console.log("patientInfo:", patientInfo);
    let patientId = patientInfo.id;
    let visitId = patientInfo.visitId;
    // let erpCustId = patientInfo.erpCustId;
    history.push({
      pathname: `/billing/${patientId}/${visitId}`,
    });
  };

  // const closeBillingInfo = () => {
  //   // No need for this function anymore since we're using routing
  // };

  const closeRoomModal = (e) => {
    setAssignRoom(false);
    setServerRes({ ...e });
    console.log("e", e);
    setTimeout(() => {
      if (isMountedRef.current) {
        closeAlert();
      }
    }, 5000);
    getProcedureList(patientInfo.id, patientInfo.visitId);
    getAssignedAllRoomlist(patientInfo.visitId);
   // getPatientProfile(patientInfo.id, patientInfo.visitId);
  };
  const closeVoucher = (e) => {
    setVoucher(false);
  };
  const closeComplaintModal = (e) => {
    setViewCmplaint(false);
    setNewCmplaint(false);
    setServerRes({ ...e });
    console.log("e", e);
    setTimeout(() => {
      if (isMountedRef.current) {
        closeAlert();
      }
    }, 5000);
    getProcedureList(patientInfo.id, patientInfo.visitId);
  };

  const getAssignedRoom = (data) => {
    console.log("each data : ", data);
    if (data && data.visitId) {
      // let patientData = data;
      // patientData['visitid'] = patientData.visitId;
      dispatch(
        doctorActions.getAssignedRoom(data, (resCb) => {
          if (resCb) {
            console.log("room assigned:", resCb);
            if (resCb.room && resCb.room.id) {
              setAssignedRoomDet(resCb.room);
              console.log("assigned room type:", resCb.room.type);
            }
          }
        })
      );
    }
  };

  const getAssignedAllRoomlist = (data) => {
    console.log("getAssignedAllRoomlist data:", data);
    if (data) {
      dispatch(
        doctorActions.getAssignedAllRoomlist(data, (resCb) => {
          console.log("API Response:", resCb);
          if (resCb) {
            console.log("all rooms assigned:", resCb);
            const roomList = Array.isArray(resCb.roomList)
              ? resCb.roomList
              : Array.isArray(resCb.assignedRoomList)
                ? resCb.assignedRoomList
                : Array.isArray(resCb.rooms)
                  ? resCb.rooms
                  : resCb.room
                    ? [resCb.room]
                    : resCb.roomData
                      ? [resCb.roomData]
                      : Array.isArray(resCb)
                        ? resCb
                        : [];
            setAssignedAllRoomDet(roomList);
          }
        })
      );
    }
  };

  // Fetches all booking-plan sessions (any status, including Cancelled) for
  // the current visit. Unlike getAssignedAllRoomlist, this is not derived
  // from room check-ins, so cancelled sessions (which never get checked in)
  // are still included.
  const getPatientSessionDetails = async (patId, vId) => {
    if (!patId || !vId) return;
    try {
      if (isMountedRef.current) {
        setPatientSessionsList([]);
      }
      const response = await bookingplanService.getBookingPlans({ main: patId, limit: 100 });
      const rows = Array.isArray(response?.data) ? response.data : [];
      const filtered = rows.filter((row) => {
        const rowPatientId = row.patient_id ?? row.patientId ?? row.patientid;
        const rowVisitId = row.visit_id ?? row.visitId ?? row.visitid ?? row.visitId;
        return String(rowPatientId) === String(patId) && String(rowVisitId) === String(vId);
      });
      if (isMountedRef.current) {
        setPatientSessionsList(filtered);
      }
    } catch (e) {
      console.error("Error fetching patient session details:", e);
    }
  };

  const getFormateDate = (date) => {
    console.log("date passed:", date);
    if (date == "" || date == null) return;
    const reqdate = new Date(date);
    let formatedate = format(reqdate, "MMM dd yyyy");
    return formatedate;
  };

  const closePdfModal = (e) => {
    console.log("Print state:", e);
    SetSeparatePdf(false);
    setPrintFiles(false);
  };
  const handleOnChange = (value, event) => {
    if (value === "date") {
      setSelecteddate(event.target.value);
      console.log("Seleected Date is", event.target.value, event);
      console.log("Seleected Date is", typeof selecteddate);
    }
    if (value === "time") {
      setSelectedTime(event.target.value);
      console.log("Seleected Date is", event.target.value, event);
      console.log("Seleected Date is", typeof selectedTime);
    }
  };

  /*
    const masterLocationDetails = useSelector(
      (state) => state.erpDetailsState.masterLocationDetails
    );
  
  const getMasterlocationDetails = async () => {
    let masterLocDetails = [];
    if(!masterLocationDetails){
      masterLocDetails = await erpActions.getAllMasterLocationDetails(dispatch);
    }else{
      masterLocDetails = masterLocationDetails;
    }
    return masterLocDetails;
  }
    const getBillingByVisitType = () =>
      helperServices.getFirstLocationIdByKey(
        masterLocationDetails,
        isOutPatient
          ? MASTER_LOCATION_DATA_LOCATION_KEY.OP_BILLING
          : MASTER_LOCATION_DATA_LOCATION_KEY.IP_BILLING
      );*/

  const validatePatientStatus = async (patient) => {
    if ((patient?.visitflag || "").toLowerCase() === "proxy") {
      setAnchorEl(null);
      setServerRes({
        success: "",
        error:
          "Please close the Proxy visit and onboard the patient as Inpatient. A Proxy visit cannot be converted directly to Inpatient.",
      });
      setTimeout(() => {
        if (isMountedRef.current) {
          closeAlert();
        }
      }, 5000);
      return;
    }

    let ServerResVal = ServerRes;

    console.log("validatePatientStatus:patientData: ", patient);

    const validateStatusResp = await erpActions.validatePatientStatus(
      patient.id,
      patient.visitId,
      patient.erpCustId
    );
    console.log("validateStatusResp: ", validateStatusResp);

    // if (!validateStatusResp) return;
    if (validateStatusResp && validateStatusResp.errorMessages) {
      console.log("error msg for validateStatusResp: ", validateStatusResp);

      ServerResVal.error = validateStatusResp.errorMessages;
      console.log("ServerResVal: ", ServerResVal);

      setServerRes({ ...ServerResVal });
      setTimeout(() => {
        if (isMountedRef.current) {
          closeAlert();
        }
      }, 5000);
    }
    // Handle success messages
    if (validateStatusResp && validateStatusResp.successMessages) {
      // if (patient && patient.type === "InPatient") {
      if (patient && patient.type === "OutPatient") {
        // setPatientCloseModal(true);
        modalPopUp();
        // setModalopen(true)
      } else {
        // patientCloseClick();
      }
    }
  };

  const cancelPatientClose = (event) => {
    setPatientCloseModal(false);
  };
  const dishchargemodal = async (event) => {
    if (isWalkinVisit) return;

    try {
      const response = await fetch('/api/v1/orgConfig', {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        let url = "/DischargeSummary/" + patientId + "/" + visitId;
        window.open(url, "_blank");
      } else {
        history.push('/Login/code');
      }
    } catch (error) {
      console.error("Session check failed:", error);
      history.push('/Login/code');
    }
  };
  return (
    <main
      className="mainsection p-0 m-0"
      onClick={(event) => closeDropDown(event)}
    >
      {errorMessages && (
        <div className="alert alert-danger text-center my-3">
          {errorMessages}
        </div>
      )}
      {ServerRes.success && (
        <Alert severity="success" className="alert-div">
          {ServerRes.success}

          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={(event) => closeAlert(event)}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Alert>
      )}
      {ServerRes.error && (
        <Alert severity="error" className="alert-div">
          {ServerRes.error}
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={(event) => closeAlert(event)}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Alert>
      )}

      <div>
        <div className="patient-profile">
          {/* {loader && (
            <div
              className="d-flex justify-content-center align-items-start h-100 m-3"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "#fff",
                zIndex: 999,
                paddingTop: "70px",
              }}
            >
              
            </div>
          )} */}
          <Card
            className="p-3 mx-3 mt-3"
            style={{
              ...PATIENT_SECTION_CARD_STYLE,
              backgroundColor: "#fff",
              fontSize: "14px",
              lineHeight: 1.45,
            }}
          >
            <div className="p-details patient-demographics">
              <div className="patient-identity cont">
                <div>
                  <span className="patient-name-line">
                    {patientInfo?.insurance !== "YES" ||
                      !activeSection.includes("Insurance") ? (
                      <span
                        className="mr-3"
                        style={{
                          fontSize: "20px",
                          fontWeight: 500,
                          lineHeight: 1.3,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                         editPatientProfile();
                         // if(!profileLoading){
                           // editPatientProfile();
                          //}
                        }}
                      >
                        {/* {patientInfo.fname} {patientInfo.mname}{" "}
                        {patientInfo.lname} */}
                        {patientInfo.fullName}
                      </span>
                    ) : (
                      <span
                        className="mr-3"
                        style={{
                          fontSize: "20px",
                          fontWeight: 500,
                          lineHeight: 1.3,
                        }}
                      >
                        {/* {patientInfo.fname} {patientInfo.mname}{" "}
                        {patientInfo.lname} */}
                        {patientInfo.fullName}
                      </span>
                    )}

                    <div className="patient-age">
                      <div
                        className="d-flex align-items-center"
                        style={{ fontSize: "14px", lineHeight: 1.3 }}
                      >
                        {!patientInfo.age ? (
                          <span>{getFormateDate(patientInfo.dob)} </span>
                        ) : (
                          <span>{patientInfo.age} yrs</span>
                        )}
                        {patientInfo.gender && (
                          <span
                            style={{
                              marginLeft: "7px",
                              minWidth: "18px",
                              height: "18px",
                              padding: "0 5px",
                              borderRadius: "9px",
                              backgroundColor: "#0288d1",
                              color: "#fff",
                              fontSize: "11px",
                              lineHeight: "18px",
                              textAlign: "center",
                              fontWeight: 600,
                            }}
                          >
                            {patientInfo.gender.charAt(0).toUpperCase()}
                          </span>
                        )}
                        {/* <span className="ml-2">{patientInfo.age} yrs</span> */}
                      </div>
                    </div>
                  </span>

                  {isVedamaayu && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginTop: "7px",
                        marginLeft: "12px",
                      }}
                    >
                      {hasActiveMembership && (
                        <span
                          role="button"
                          tabIndex={0}
                          title="View membership services"
                          onClick={openMembershipServices}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openMembershipServices();
                            }
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            width: "fit-content",
                            padding: "3px 9px",
                            borderRadius: "12px",
                            backgroundColor: "#fff4cc",
                            color: "#805b00",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          <i
                            className="material-icons"
                            style={{ fontSize: "14px", marginRight: "4px" }}
                          >
                            workspace_premium
                          </i>
                          Active Member
                        </span>
                      )}

                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Start Membership"
                        title="Start a patient membership"
                        onClick={openMembershipDialog}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openMembershipDialog();
                          }
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "26px",
                          height: "26px",
                          border: "1px solid #bbdefb",
                          borderRadius: "50%",
                          backgroundColor: "#eef6ff",
                          color: "#1565c0",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="material-icons"
                          style={{ fontSize: "17px" }}
                        >
                          card_membership
                        </i>
                      </span>
                    </div>
                  )}

                  <div className="patient-registration">
                    <p
                      className="mb-1 mx-1 text-muted"
                      style={{ display: "inline" }}
                    >
                      Registered On: {regDate}
                    </p>
                  </div>
                </div>
              </div>
              <div className="patient-demographic-details cont">
                <div className="patient-detail-group cont">
                  <p className="mb-0 d-flex">
                    <i class="material-icons p-icon">school</i>{" "}
                    <span className="p-data">
                      {patientInfo.education ? patientInfo.education : "NA"}
                    </span>
                  </p>
                  <p className="mb-0 d-flex">
                    <i class="material-icons p-icon">bloodtype</i>
                    <span className="p-data">
                      {patientInfo.bloodgroup ? patientInfo.bloodgroup : "NA"}
                    </span>
                  </p>
                  <p className="mb-0 d-flex">
                    <i class="material-icons p-icon">health_and_safety</i>
                    <span className="p-data">
                      {patientInfo.insurance == "YES"
                        ? "Insured"
                        : "Not Insured"}
                    </span>
                  </p>
                </div>
                <div className="patient-detail-group cont">
                  <p className="mb-0 d-flex">
                    <i class="material-icons p-icon">phone</i>{" "}
                    <span className="p-data">
                      {patientInfo.phno ? patientInfo.phno : "NA"}
                    </span>
                  </p>
                  <p className="mb-0 d-flex">
                    <i class="material-icons p-icon">mail</i>{" "}
                    <span className="p-data">
                      {patientInfo.email ? patientInfo.email : "NA"}
                    </span>
                  </p>
                  <p className="mb-0 d-flex">
                    <i className="material-icons p-icon">credit_card</i>
                    {/* {org?.isAadharNumberMandatory?.forInpatients &&
                    patientInfo?.type === "InPatient" &&
                    !patientInfo.adhar ? (
                      <div>
                        <Alert
                          severity="warning"
                          className="alert-icon"
                          sx={{
                            padding: "2px 4px",
                            fontSize: "0.875rem",
                            lineHeight: "0.5",
                            minHeight: "unset",
                            backgroundColor: "transparent",
                            "& .MuiAlert-icon": {
                              color: "warning.main",
                              padding: 0,
                              marginRight: "4px",
                            },
                          }}
                        ></Alert>
                      </div>
                    ) : (
                      <span className="p-data">{patientInfo.adhar || "—"}</span>
                    )} */}
                    <span className="p-data">{patientInfo.adhar || "—"}</span>
                  </p>
                  {/* <p className="mb-0 d-flex"><i class="material-icons p-icon">numbers</i> <span className="p-data">{patientInfo.fileNo ? patientInfo.fileNo : 'NA'}</span></p> */}
                </div>
                {/* <div className='col-lg-4 col-md-4 col-xs-1 col-sm-1 cont'>
                        <p className='mb-0 d-flex'><i class="material-icons p-icon">file</i>  <span  className="p-data">{patientInfo.fileNo}</span></p>
                        <p className="mb-0 d-flex"><i class="material-icons p-icon">reference</i> <span className="p-data">{patientInfo.srcRef}</span></p>
                    </div> */}
                <div className="patient-detail-group cont">
                  <p className="mb-0 d-flex">
                    <i class="material-icons p-icon">place</i>&nbsp;
                    <span className="d-flex flex-column p-data">
                      <span>{patientInfo.address}</span>
                      <span>{patientInfo.area}</span>
                      <span>{patientInfo.city}</span>
                      <span>{patientInfo.state}</span>
                      <span>{patientInfo.pincode}</span>
                    </span>
                  </p>
                  <p key={desc} className="mb-0 d-flex">
                    <i class="material-icons p-icon">person</i>{" "}
                    <span className="p-data">{desc ? desc : "NA"}</span>
                  </p>
                </div>
                {/* {org.isAadharNumberMandatory.forInpatients &&
                patientInfo.type === 'InPatient' && 
                !patientInfo.adhar && (
                <>
        
                  <div style={{ 
                  marginLeft: '220px',  
                  marginTop: '4px',
                  marginBottom: '12px',  
                  width: 'fit-content'
                  }}>
                  <Alert severity="warning" sx={{ 
                    padding: '2px 8px', 
                    fontSize: '0.875rem',
                    lineHeight: '1',      
                    minHeight: 'unset'    
                  }}>
                  Please submit Aadhaar details before Discharge
                  </Alert>
                  </div>
                    </>
                  )} */}

                {/* {!activeSection.includes("Insurance") && ( */}
                {/* {(patientInfo?.insurance != "YES" ||
                  !activeSection.includes("Insurance")) && (
                  <div className="col-lg-12 col-md-12 col-xs-12 col-sm-12 d-flex justify-content-end">
                    <Button
                      className="btn btn-submit btn-primary ml-3"
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ textTransform: "capitalize" }}
                      onClick={() => editPatientProfile()}
                    >
                      Edit Profile
                    </Button>
                  </div>
                )} */}
              </div>
            </div>
          </Card>

          {!activeSection.includes("Insuranc") && (
            <div className="my-4 mx-3">
              <div className="row align-items-stretch">
                <div className="col-lg-8 col-md-12 col-xs-12 col-sm-12 d-flex">
                  <Card
                    className="p-3 patient-summary h-100 w-100"
                    style={PATIENT_SECTION_CARD_STYLE}
                  >
                    <span
                      className="d-flex align-items-baseline justify-content-between flex-wrap"
                      style={{
                        paddingBottom: "12px",
                        marginBottom: "12px",
                        borderBottom: "1px solid #e4edf6",
                      }}
                    >
                      <span className="col-lg-8 col-md-8 col-sm-12 col-xs-12 d-flex flex-lg-row flex-md-column flex-wrap align-items-baseline px-0">
                        <h4 className="mb-1 pl-0" style={PATIENT_PRIMARY_TITLE_STYLE}>
                          Visit Summary&nbsp;
                        </h4>
                        <p style={PATIENT_META_TEXT_STYLE}>
                          {" "}
                          ({patientInfo.visitflag || patientInfo.type}
                          {/* {patientInfo.department ? ", " + getDepartmentName(patientInfo.department) : ""} */}
                          {patientInfo.department
                            ? ", " + getDepartmentName(patientInfo.department)
                            : ""}
                          , &nbsp;
                          {getFormateDate(patientInfo.visitDate)}, &nbsp;
                          {patientInfo.status}&nbsp;
                          {org?.orgCode === ORG_CODES.MANASWINI &&
                            isOverrided === true &&
                            ", Registration fee pending"}
                          )
                        </p>
                      </span>

                      <span className="mb-4 col-md-4 col-lg-4 col-sm-12 col-xs-12 d-flex justify-content-end">
                        {/* {patientInfo.status == "close" &&
                          currentStatus === null && (
                            <span className="mr-2">
                              <Button
                                className="btn-primary btn-block"
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: "capitalize", height: "33px" }}
                                onClick={openOnboardModal}
                              >
                                Onboard
                              </Button>
                            </span>
                          )} */}
                        {patientInfo.status == "preReg" ||
                          (patientInfo.status == "booked" && (
                            <span className="mr-2">
                              <button
                                className="btn btn-primary btn-block"
                                onClick={openPayment}
                              >
                                Pay Now
                              </button>
                            </span>
                          ))}

                        <span className="mr-2">
                          <div
                            class="printAction"
                            open={printAction}
                            toggle={(event) => printActions(event)}
                            group
                          >
                            {patientInfo.status == "close" &&
                              currentStatus === null && (
                                <Button
                                  sx={{
                                    textTransform: "capitalize",
                                    height: "33px",
                                    marginRight: "2px",
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                  }}
                                  className="btn-primary btn-block"
                                  variant="contained"
                                  color="primary"
                                  onClick={openOnboardModal}
                                >
                                  Onboard
                                </Button>
                              )}
                            <Button
                              sx={{
                                textTransform: "capitalize",
                                height: "auto",
                                minHeight: "33px",
                                borderRadius: "0px",
                                marginRight: "2px",
                                whiteSpace: "nowrap",
                                lineHeight: 1.3,
                              }}
                              variant="contained"
                              color="primary"
                              onClick={printPatientCard}
                            >
                              Print Patient Card
                            </Button>

                            <Button
                              sx={{
                                textTransform: "capitalize",
                                height: "33px",
                                borderRadius: "0px",
                                // borderTopRightRadius: 0,
                                // borderBottomRightRadius: 0,
                              }}
                              variant="contained"
                              color="primary"
                              className="mr-2"
                              onClick={checkScanner}
                            >
                              Scan
                            </Button>
                            <Button
                              sx={{
                                textTransform: "capitalize",
                                marginLeft: "2px",
                                height: "33px",
                                borderRadius: 0,
                                borderBottomLeftRadius: 0,
                              }}
                              id="bornon"
                              variant="contained"
                              color="primary"
                              onClick={printAll}
                              aria-controls="print-menu"
                              aria-haspopup="true"
                            >
                              Print
                            </Button>

                            <Select
                              split
                              id="dr"
                            // onClick={e => e.stopPropagation()}
                            >
                              {org?.orgCode !== ORG_CODES.VEDAMAAYU && (
                                <MenuItem onClick={() => tokenPrint()}>
                                  Print Token
                                </MenuItem>
                              )}
                              <MenuItem
                                onClick={() =>
                                  props.history.push(
                                    `/PatientSections/${props.match.params.patientId}/${props.match.params.visitId}/consent-forms`
                                  )
                                } 
                              >
                                Consent Forms
                              </MenuItem>
                              <MenuItem onClick={downloadBarcode}>
                                Print Barcode
                              </MenuItem>
                              {org?.isCounsellor?.display && org?.orgCode !== ORG_CODES.VEDAMAAYU && (
                                <MenuItem onClick={() => console.log("Print Receipt")}>
                                  Print Receipt
                                </MenuItem>
                              )}
                              <MenuItem onClick={() => console.log("Print IDCard")}>
                                Print IDCard
                              </MenuItem>
                            </Select>
                          </div>
                        </span>
                        <>
                          <IconButton
                            id="ic1"
                            aria-controls="simple-menu"
                            aria-haspopup="true"
                            onClick={handleClick}
                            size="small"
                            className="mr-2"
                          >
                            <MoreVertIcon id="we1" />
                          </IconButton>
                          <Menu
                            id="simple-menu "
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                          >
                            {activeSection.includes("Complaints") && (
                              <MenuItem onClick={(event) => newComplaints(event)}>
                                Complaints
                              </MenuItem>
                            )}
                            {activeSection.includes("AllocateRoom") &&
                              !activeSection.includes("Insurance") &&
                              patientInfo.type === "InPatient" &&
                              !isWalkinVisit &&
                              patientInfo.status !== "close" && (
                                <MenuItem onClick={(event) => assignRooms(event)}>
                                  Assign Rooms
                                </MenuItem>
                              )}
                            {/* {activeSection.includes("Insurance") &&
                              patientInfo.type === "InPatient" && (
                                <MenuItem
                                  onClick={(event) => insuranceDeskDetails(event)}
                                >
                                  Insurance Desk
                                </MenuItem>
                              )} */}

                            {activeSection.includes("NonRedeemableVouchers") && (
                              <MenuItem onClick={(event) => voucherInfo(event)}>
                                Vouchers
                              </MenuItem>
                            )}
                            {org?.orgCode === ORG_CODES.MNH &&
                              activeSection.includes("AllocateRoom") &&
                              patientInfo.type === "InPatient" &&
                              patientInfo.status !== "close" &&
                              patientInfo.gender === "Female" && (
                                <MenuItem onClick={(event) => editdepndents(event)}>
                                  Add Dependents
                                </MenuItem>
                              )}

                            {patientInfo &&
                              patientInfo.status === "open" &&
                              patientInfo.type === "OutPatient" ? (
                              <MenuItem
                                onClick={() => validatePatientStatus(patientInfo)}
                              // disabled={
                              //   patientInfo.internalStatus ===
                              //     "MOVED_TO_BILLING" ||
                              //   patientInfo.internalStatus ===
                              //     "INVOICE_GENERATED" ||
                              //   patientInfo.internalStatus === "BILL_CLEARED"
                              // }
                              >
                                Convert To Inpatient
                              </MenuItem>
                            ) : null}

                            <MenuItem onClick={(event) => viewLastVisit(event)}>
                              Last Visits
                            </MenuItem>

                            <MenuItem
                              onClick={() => {
                                handleClose();
                                window.open(
                                  `/procedure-list/${props.match.params.patientId}/${props.match.params.visitId}`,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                            >
                              Prescription Listing
                            </MenuItem>

                            <MenuItem onClick={(event) => billingInfo(event)}>
                              Billable
                            </MenuItem>

                            {patientInfo &&
                              patientInfo.type === "InPatient" &&
                              !isWalkinVisit && (
                                <MenuItem onClick={(event) => dishchargemodal(event)}>
                                  Discharge Summary
                                </MenuItem>
                              )}

                            {org?.orgCode === ORG_CODES.MANASWINI &&
                              patientInfo &&
                              (patientInfo.status === "preReg" ||
                                patientInfo.status === "booked" ||
                                isOverrided == true) && (
                                <MenuItem onClick={openPayment}>Pay Now</MenuItem>
                              )}
                          </Menu>
                        </>
                      </span>
                    </span>

                    <div className="row mainId">
                      <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                        <h6 className="mb-1">MAIN</h6>
                        <span className="d-flex align-items-center justify-content-between">
                          <span className="text-muted">{patientInfo.id}</span>
                        </span>
                      </div>
                    </div>
                    <div className="row mt-2">
                      {patientInfo.type == "InPatient" ? (
                        <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                          <h6 className="mb-1">Admission Date</h6>
                          <span className="d-flex align-items-center justify-content-between">
                            <span className="text-muted">
                              {patientInfo.admsnDate}
                            </span>
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                      {patientInfo.type == "InPatient" &&
                        patientInfo.dischargeDate != "" ? (
                        <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                          <h6 className="mb-1">Discharge Date</h6>
                          <span className="d-flex align-items-center justify-content-between">
                            <span className="text-muted">
                              {patientInfo.dischargeDate}
                            </span>
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="row mt-2">
                      {patientInfo.type == "OutPatient" ? (
                        <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                          <h6 className="mb-1">Token</h6>
                          <span className="d-flex align-items-center justify-content-between">
                            <span className="text-muted">
                              {patientInfo.tokenNumber}
                            </span>
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                      {patientInfo.type == "OutPatient" ? (
                        <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                          <h6 className="mb-1">Desk Number</h6>
                          <span className="text-muted">
                            {patientInfo.deskNumber}
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="row mt-2">
                      {patientInfo && patientInfo.status != "close" ? (
                        <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                          <h6 className="mb-1">Bar Code</h6>
                          <span className="d-flex align-items-center justify-content-between">
                            <canvas
                              id="mybarcode"
                              ref={inputRef}
                              style={{ maxWidth: "150px" }}
                            />
                          </span>
                        </div>
                      ) : (
                        <canvas
                          id="mybarcode"
                          ref={inputRef}
                          style={{ maxWidth: "150px", display: "none" }}
                        />
                      )}

                      <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                        <h6 className="mb-1">Complaints</h6>

                        {complaintText && complaintText.length > 0 ? (
                          <>
                            <div className="complaints-det">
                              {complaintText.map((list, idx) => {
                                const words = list.text.split(" ");
                                const shortText =
                                  words.length > 2
                                    ? `${words.slice(0, 5).join(" ")}...`
                                    : list.text;

                                return (
                                  <div
                                    key={idx}
                                    className="complaints-det my-2 justify-content-between text-muted"
                                  >
                                    <span title={list.text}>{shortText}</span>
                                  </div>
                                );
                              })}
                            </div>
                            {complaintText.length > 2 && (
                              <div>
                                <a
                                  className="more"
                                  onClick={(e) => viewComplaints(e)}
                                >
                                  view more
                                </a>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-muted">No complaints yet</div>
                        )}
                      </div>

                      <div className="col-lg-4 col-md-4 col-xs-12 col-sm-12">
                        <h6 className="mb-1">Stay Room</h6>
                        {stayRooms && stayRooms.length > 0 ? (
                          <div className="complaints-det ">
                            {stayRooms.map((room) => renderRoomItem(room, 'stay'))}
                          </div>
                        ) : lastAsssigned?.room && !lastAssignedIsSessionRoom ? (
                          <div className="complaints-det">
                            <p className="my-2" style={{ color: "#0b4623", fontSize: "13px", fontWeight: 500 }}>
                              {lastAsssigned.room}
                            </p>
                          </div>
                        ) : (
                          <div className="text-muted">No Stay Room Assigned</div>
                        )}
                      </div>

                      </div>
                    
                    {/* </div> */}
                  </Card>
                </div>

                <div className="col-lg-4 col-md-12 col-xs-12 col-sm-12 d-flex">
                  <div
                    className={`appointmnt-det d-flex flex-column h-100 w-100 px-sm-2 px-md-0 px-lg-2 ${
                      showCounsellorAppointment ? "justify-content-between" : ""
                    }`}
                  >
                    <Components.SearchDocList
                      docInfo={docInfo}
                      doctors={personDetailsService.getDoctorsDetail()}
                      from="patienSection"
                      patientInfo={patientInfo}
                      apptdate={apptdate}
                      rootClassName={!showCounsellorAppointment ? "h-100" : ""}
                      cardClassName={
                        !showCounsellorAppointment
                          ? "h-100 d-flex flex-column justify-content-center"
                          : ""
                      }
                      cardStyle={{
                        ...PATIENT_SECTION_CARD_STYLE,
                        padding: "16px",
                      }}
                      headerStyle={{
                        width: "100%",
                        paddingBottom: "12px",
                        marginBottom: "12px",
                        borderBottom: "1px solid #e4edf6",
                      }}
                      headingColor="#174a7e"
                      headingStyle={PATIENT_SECTION_TITLE_STYLE}
                      patientCalenderInfo={patientCalenderInfoD}
                      lastassigned={lastAsssigned}
                      activeSection={activeSection}
                      parentCallBack={(event) => handleDocChange(event)}
                      onModalClose={(event) => updatePatientDetails(event)}
                    />

                    {showCounsellorAppointment && (
                      <Components.Counsellor
                        from="patienSection"
                        counsInfo={counsInfo}
                        patientInfo={patientInfo}
                        patientCalenderInfo={patientCalenderInfoC}
                        lastassigned={lastAsssigned}
                        activeSection={activeSection}
                        parentCallBack={(event) => handleCounslChange(event)}
                        onModalClose={(event) => updatePatientDetails(event)}
                      />
                    )}
                  </div>
                </div>
              </div>
{/* 
              {sessionRooms && sessionRooms.length > 0 && ( */}
                <div className="row mt-4">
                  <div className="col-12">
                    <Card
                      className="p-3 mt-0"
                      style={PATIENT_SECTION_CARD_STYLE}
                    >
                      <div
                        className="d-flex align-items-center justify-content-between mb-3"
                        style={{
                          paddingBottom: "12px",
                          borderBottom: "1px solid #e4edf6",
                        }}
                      >
                        <h6 className="mb-0 pl-0" style={PATIENT_SECTION_TITLE_STYLE}>
                          Session Details
                        </h6>
                        {patientInfo?.status !== "close" && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ textTransform: "capitalize", minWidth: "auto", fontSize: "12px" }}
                            onClick={handleAddSession}
                          >
                            + Add Session
                          </Button>
                        )}
                      </div>
                      <div
                        style={{
                          maxHeight: '420px',
                          overflowY: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                        }}
                      >
                        {patientSessionsList
                          .slice()
                          .sort((a, b) => new Date(`${a.start_date}T${a.start_time || "00:00:00"}`) - new Date(`${b.start_date}T${b.start_time || "00:00:00"}`))
                          .map((session, sessionIdx, sessionsArr) => {
                            const roomName = session?.room_number || '—';
                            const treatmentName = session?.treatment_name || '';
                            const packageService = session?.package_service_name || session?.package_name || '';
                            const doctorName = session?.doctor_name || '';
                            const packageService2 = session?.package_service_name2 || session?.package_name2 || '';
                            const doctorName2 = session?.doctor_name2 || '';
                            const hasSecondTreatment = Boolean(packageService2 || doctorName2);
                            // Build the session fraction displayed beside the primary treatment.
                            // Fall back to total_days for older booking-plan records and use 1/1
                            // when session information is unavailable.
                            const currentSession = Number(session?.session_number) || 1;
                            const totalSessions =Number(session?.total_sessions) || 1;
                            const sessionFraction = `${currentSession}/${totalSessions}`;

                            // Build the fraction for the optional second treatment.
                            // Keep it empty when the second treatment has no session information.
                            const totalSessions2 =Number(session?.total_sessions2) || 0;
                            const currentSession2 = Number(session?.session_number2) || 1;
                            const sessionFraction2 =
                              totalSessions2 > 0
                                ? `${currentSession2}/${totalSessions2}`
                                : '';
                            const statusText = session?.status || '';
                            const statusStyle = getStatusBadgeStyle(statusText);
                            // A "Modified" session was superseded by a reassign (edited into a
                            // new record) — treat it like Cancelled: no Edit button on the dead row.
                            const isCancelled = ['cancelled', 'modified'].includes(statusText.toLowerCase());
                            const isLast = sessionIdx === sessionsArr.length - 1;

                            return (
                              <div
                                key={`session-${session.id}`}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '150px 56px minmax(260px, 1fr) 140px 36px',
                                  alignItems: 'center',
                                  columnGap: '16px',
                                  padding: '12px 6px',
                                  borderBottom: isLast ? 'none' : '1px solid #eef0f2',
                                }}
                              >
                                <div style={{ display: 'grid', rowGap: '5px' }}>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                                  {formatSessionDate(session?.start_date)}
                                </span>
                                <span style={{ fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>
                                  {formatSessionTime(session?.start_time)} - {formatSessionTime(session?.end_time)}
                                </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    backgroundColor: '#eef2f7',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    color: '#333',
                                    textAlign: 'center',
                                    width: 'fit-content',
                                  }}
                                >
                                  {roomName}
                                </span>
                               
                                <div style={{ display: 'grid', rowGap: '5px', minWidth: 0 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 220px) 50px minmax(0, 1fr)', columnGap: '10px' }}>
                                    <span style={{ fontSize: '13px', color: '#333' }}>{packageService}</span>
                                    <span style={{ fontSize: '13px', color: '#333' }}>{sessionFraction}</span>
                                    <span style={{ fontSize: '13px', color: '#7a7a7a' }}>{doctorName}</span>
                                  </div>
                                  {hasSecondTreatment && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 220px) 50px minmax(0, 1fr)', columnGap: '10px' }}>
                                      <span style={{ fontSize: '13px', color: '#333' }}>{packageService2}</span>
                                      <span style={{ fontSize: '13px', color: '#333' }}>{sessionFraction2}</span>
                                      <span style={{ fontSize: '13px', color: '#7a7a7a' }}>{doctorName2}</span>
                                    </div>
                                  )}
                                </div>
                                {/* <span style={{ fontSize: '13px', color: '#555' }}>
                                  {formatSessionTime(session?.start_time)} - {formatSessionTime(session?.end_time)}
                                </span> */}
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    whiteSpace: 'nowrap',
                                    width: 'fit-content',
                                    ...statusStyle,
                                  }}
                                >
                                  {statusText}
                                </span>
                                {patientInfo?.status !== "close" && !isCancelled ? (
                                  <IconButton
                                    size="small"
                                    disabled={!isEditWindowOpen(session)}
                                    onClick={() => isEditWindowOpen(session) && handleEditSession({ bookingId: session.id })}
                                    sx={{
                                      padding: '4px',
                                      backgroundColor: '#f0f7ff',
                                      color: '#1976d2',
                                      justifySelf: 'end',
                                      '&:hover': {
                                        backgroundColor: '#e3f2fd',
                                      },
                                      '&.Mui-disabled': {
                                        backgroundColor: '#f1f1f1',
                                        color: '#b0b0b0',
                                      },
                                    }}
                                    title={isEditWindowOpen(session) ? 'Edit Session' : 'Editing is locked after 10 PM on the day this session was made'}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </IconButton>
                                ) : (
                                  <span />
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </Card>
                  </div>
                </div>
              {/* )} */}

              {isVedamaayu && (
                <MembershipUsageHistory
                  patientId={patientId || props.match?.params?.patientId}
                  visitId={visitId || props.match?.params?.visitId}
                />
              )}
            </div>
          )}

          {activeSection.includes("Insurance") &&
            patientInfo.insurance == "YES" &&
            patientInfo.type == "InPatient" && (
              <>
                <canvas
                  id="mybarcode"
                  ref={inputRef}
                  style={{ maxWidth: "150px", display: "none" }}
                />

                <Components.InsuranceProcedureListing
                  key={patientInfo.visitDate}
                  patientProcedureList={patientProcedureList}
                  patientInfo={patientInfo}
                  addInsurance="true"
                  allVisitdates={allVisitdates}
                  selectedDate={selectedDate}
                  handlevisitChange={(e) => handleChange(e)}
                  onClose={(event) => updateInsuranceProcedure(event)}
                />
              </>
            )}

          <Dialog
            open={isVedamaayu && membershipDialogOpen}
            onClose={closeMembershipDialog}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: "8px",
                width: "720px",
                maxWidth: "calc(100% - 32px)",
                minHeight: "360px",
              },
            }}
          >
            <DialogTitle sx={{ px: 4, pt: 3.5, pb: 2 }}>
              Start Membership
              <IconButton
                aria-label="close"
                onClick={closeMembershipDialog}
                disabled={membershipLoading}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 4, pb: 2.5 }}>
              <p className="mb-2 text-muted" style={{ lineHeight: 1.5 }}>
                Select the purchased plan and enter its invoice number for {patientInfo.fullName || patientInfo.name || "this patient"}.
              </p>
              <TextField
                select
                fullWidth
                margin="normal"
                label="Membership Plan"
                value={selectedMembershipPlanId}
                onChange={(event) => setSelectedMembershipPlanId(event.target.value)}
                disabled={membershipLoading}
                sx={{ mt: 2.5, mb: 1.5 }}
              >
                {membershipPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name} ({plan.validity} days)
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                margin="normal"
                label="Invoice Number"
                placeholder="Example: VIP26-27/11"
                value={membershipReceiptNo}
                onChange={(event) =>
                  setMembershipReceiptNo(event.target.value.toUpperCase())
                }
                disabled={membershipLoading}
                inputProps={{ maxLength: 255 }}
                sx={{ mt: 1.5, mb: 0.5 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 4, pt: 2, pb: 3.5, gap: 1.5 }}>
              <Button
                onClick={closeMembershipDialog}
                disabled={membershipLoading}
                sx={{ textTransform: "capitalize" }}
              >
                Cancel
              </Button>
              <Button
                onClick={startMembership}
                disabled={
                  membershipLoading ||
                  !selectedMembershipPlanId ||
                  !membershipReceiptNo.trim()
                }
                variant="contained"
                color="primary"
                sx={{ textTransform: "capitalize" }}
              >
                {membershipLoading ? "Starting..." : "Start Membership"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            className="d-flex w-95 h-90 flex-column-reverse "
            id="closemodalopen"
            open={modalopen}
            toggle={toggle}
          >
            <div className="w-95 h-120">
              <DialogTitle>
                Convert To InPatient
                <IconButton
                  aria-label="close"
                  onClick={toggle}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <span>
                  Are you sure want to convert OutPatient to InPatient?
                </span>
                <div className="d-flex m-2">
                  <div>
                    <label style={{ paddingTop: "5px" }}>
                      Select a Admission Date:
                    </label>
                    <div className="d-flex pl-0 col-lg-7 col-xl-7 col-md-12 col-12">
                      {/* Date Input */}
                      <div className="mr-2" style={{ marginRight: "20px" }}>
                        <input
                          sx={{ marginRight: "10px" }}
                          name="admissionDate"
                          id="admissionDate"
                          className="form-control filterin Quantity"
                          placeholder="Enter Scheduled Date"
                          required
                          type="date"
                          defaultValue={selecteddate}
                          onChange={(event) => handleOnChange("date", event)}
                        />
                      </div>
                      {/* Time Input */}
                      <div className="ml-2" sx={{ marginLeft: "10px" }}>
                        <input
                          name="admissionTime"
                          id="admissionTime"
                          className="form-control filterin Quantity"
                          placeholder="Enter Scheduled Time"
                          required
                          type="time"
                          defaultValue={selectedTime}
                          onChange={(event) => handleOnChange("time", event)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>

              <DialogActions>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ textTransform: "none" }}
                  className="btn btn-submit btn-primary btn-style bottomright"
                  onClick={convertToInPatient}
                >
                  Convert
                </Button>
              </DialogActions>
            </div>
          </Dialog>
          {/* <Dialog
      open={modalopen}
      onClose={toggle}
      className="d-flex flex-column-reverse"
    >
      <DialogTitle>
        Warning!
        <IconButton
          aria-label="close"
          onClick={toggle}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <span>
          Are you sure you want to convert OutPatient to InPatient?
        </span>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={convertToInPatient}
          sx={{textTransform:'none'}}
        >
          Convert
        </Button>
      </DialogActions>
    </Dialog> */}

          {/* Display Last Visit Modal */}

          <Dialog
            className="d-flex w-95  flex-column-reverse h-180 modalDialog1"
            id="visitModal"
            open={viewVisit}
            toggle={toggleVisitModal}
          >
            <div>
              <DialogTitle>
                View Last Visits
                {/* <span className="close-model" onClick={() => toggleVisitModal()}>
                X
              </span> */}
                <IconButton
                  aria-label="close"
                  onClick={() => toggleVisitModal()}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <div>
                  <Components.VisitList
                    allVisitdates={allVisitdates}
                    selectedDate={selectedDate}
                    handlevisitChange={(e) => handlevisitSelect(e)}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                {/* <Button
               variant="contained"
               color="primary"
              // className="btn btn-submit btn-primary btn-style bottomright"
                
                onClick={() => handleSelect()}
              >
                OK 
              </Button> */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSelect}
                  sx={{ textTransform: "none" }}
                >
                  OK
                </Button>
              </DialogActions>
            </div>
          </Dialog>
        </div>
      </div>

      <Dialog
        id="patientRevisit"
        open={onBoardmodal}
        onClose={toggleonboard} // Use the onClose event handler to handle modal closing
        className="d-flex w-95 h-90 flex-column-reverse dialog"
      >
        <div>
          <DialogTitle>
            Select Patient Category
            <IconButton
              aria-label="close"
              onClick={(event) => toggleonboard(event)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: "0px 24px" }}>
            <span>Are you sure to OnBoard a patient?</span>
          </DialogContent>

          <DialogActions
            className="d-flex flex-column align-items-end"
            style={{ paddingTop: "0px" }}
          >
            <RadioGroup
              row
              aria-label="patientCategory"
              name="patientCategory"
              value={selectedRadio}
              onChange={(event) => onValueChange(event.target.value)}
              className="d-flex w-100 justify-content-between"
            >
              <FormControlLabel
                value="InPatient"
                control={<Radio />}
                label="InPatient"
              />
              <FormControlLabel
                value="OutPatient"
                control={<Radio />}
                label="OutPatient"
              />
              {org?.isOnlinePatient?.display &&
                org?.orgCode !== ORG_CODES.VEDAMAAYU && (
                  <FormControlLabel
                    value="OnlinePatient"
                    control={<Radio />}
                    label="OnlinePatient"
                  />
                )}
              {org?.orgCode === ORG_CODES.VEDAMAAYU && (
                <FormControlLabel
                  value="Walkin"
                  control={<Radio />}
                  label="Walk In"
                />
              )}
              <FormControlLabel
                value="Proxy"
                control={<Radio />}
                label="Proxy"
              />
            </RadioGroup>
            {selectedRadio === "InPatient" ||
            selectedRadio === "Walkin" ? (
              <div
                className="d-flex justify-content-end"
                style={{
                  flexDirection: "column",
                  padding: "0px 10px",
                  marginRight: "auto",
                }}
              >
                <label style={{ paddingTop: "5px", paddingBottom: "20px" }}>
                  Select a Admission Date:
                </label>
                <Grid container spacing={2} alignItems="center">
                  {/* Date Input */}
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <TextField
                      name="admissionDate"
                      id="admissionDate"
                      type="date"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      defaultValue={selecteddate}
                      //  defaultValue={selecteddate}
                      onChange={(event) => handleOnChange("date", event)}
                    />
                  </Grid>

                  {/* Time Input */}
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <TextField
                      name="admissionTime"
                      id="admissionTime"
                      type="time"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      defaultValue={selectedTime}
                      onChange={(event) => handleOnChange("time", event)}
                    />
                  </Grid>
                </Grid>
              </div>
            ) : (
              ""
            )}
            <Button
              variant="contained"
              color="primary"
              className="btn-style bottomright mt-2"
              onClick={onboardPatient}
              style={{ textTransform: "none", marginRight: "15px" }}
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      <Dialog
        open={patientCloseModal}
        onClose={toggle} // Use the onClose event handler to handle modal closing
        className="d-flex w-95 h-90 flex-column-reverse modalDialog"
      >
        <div>
          <DialogTitle>
            Warning!
            <IconButton
              aria-label="close"
              onClick={(event) => toggle(event)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="billing">
            <span>
              {" "}
              Are you sure you want to close the visit of{" "}
              {patientInfo ? patientInfo.fname : ""}{" "}
              {patientInfo ? patientInfo.lname : ""} (
              {patientInfo ? patientInfo.id : ""}) ?
            </span>
          </DialogContent>
          <DialogActions className="moveToBill">
            <div
              className="mx-3"
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingTop: "10px",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  className="btn-style bottomright"
                  onClick={(event) => cancelPatientClose(event)}
                  style={{ textTransform: "none" }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  className="btn-style bottomright"
                  onClick={(event) => convertToInPatient(event)}
                  style={{ textTransform: "none" }}
                >
                  Submit
                </Button>
              </div>
            </div>
          </DialogActions>
        </div>
      </Dialog>

      {openPatientModal && (
        <Components.PatientForm
          patientInfo={patientInfo}
          deskDetails={deskDetails}
          openPatientForm={openPatientModal}
          type="AdminHomeView"
          openOnboardModal={(event) => openOnboardModal(event)}
          closePatientForm={(event) => editPatientProfile(event)}
          onSuccess={(event) => updatePatientDetails(event)}
        />
      )}

      {/* {schduleApp &&  (
            <Components.AppointmentScheduleModal
              from="list"
              counsInfo={counsInfo}
              type="Counsellor"
              patientData={patientInfo}
            
              onModalClose={(event) => closeAppointmentModal(event)}
            />
         
          )} */}

      {viewCmplaint && (
        <Components.Complaints
          patientInfo={patientInfo}
          viewComplaint={viewCmplaint}
          allVisitdates={allVisitdates}
          selectedDate={selectedDate}
          handlevisitChange={(event) => handleChange(event)}
          onCloseComplaintModal={(event) => closeComplaintModal(event)}
        />
      )}
      {newCmplaint && (
        <Components.Complaints
          patientInfo={patientInfo}
          newComplaint={newCmplaint}
          allVisitdates={allVisitdates}
          selectedDate={selectedDate}
          handlevisitChange={(event) => handleChange(event)}
          onCloseComplaintModal={(event) => closeComplaintModal(event)}
        />
      )}
      {assignRoom && (
        <Components.Room
          patientInfo={patientInfo}
          assignRoom={assignRoom}
          allVisitdates={allVisitdates}
          selectedDate={selectedDate}
          handlevisitChange={(event) => handleChange(event)}
          closeRoomModal={(event) => closeRoomModal(event)}
        />
      )}

      {addedependent && (
        <Components.PatientForm
          patientInfo={patientInfo}
          deskDetails={deskDetails}
          openPatientForm={addedependent}
          type="AdminHomeView"
          isDependent={addedependent}
          openOnboardModal={(event) => openOnboardModal(event)}
          closePatientForm={(event) => editdepndents(event)}
          onSuccess={(event) => updatePatientDetails(event, "dependent")}
        />
      )}

      {insuranceDesk && (
        <Components.InsuranceProcedureListing
          patientProcedureList={patientProcedureList}
          patientInfo={patientInfo}
          addInsurance={insuranceDesk}
          onClose={(event) => updateInsuranceProcedure(event)}
        />
      )}

      {voucher && (
        <Components.Vouchers
          addVouchers={voucher}
          onCloseVoucher={(event) => closeVoucher(event)}
        />
      )}
      
      {PrintFiles && (
        <div className="">
          <Components.PrintFiles
            patientDetails={patientInfo}
            deskDetails={deskDetails}
            tokenDetails={patientInfo}
            barcode={arrayui}
            printOption={{
              token: true,
              barcode: true,
              receipt: true,
              idcard: true,
              surgeryConsent: true,
              panchakarmaConsent: true,
              admissionConsent: true,
              painAssessment: true,
            }}
            onModalClose={(event) => closePdfModal(event)}
          />
        </div>
      )}
      {patientCardPrint && (
        <Components.PrintPatientCard
          patientInfo={{
            ...patientInfo,
            patientId: props.match.params.patientId,
            visitId: props.match.params.visitId,
            roomName: assignedRoomDet?.roomName || "—",
            doctorName: currentDoctorName,
            counsellorName: currentCounselorName,
            locationId: locationId,
            locationType: patientInfo?.type === "OutPatient" ? "OP" : "IP",
            ipopnumber: patientInfo?.ipopnumber || "—",
          }}
          regDate={regDate}
          parentCallBack={() => setPatientCardPrint(false)}
        />
      )}
      {surgeryConsentPrint && (
        <Components.PrintSurgeryConsent
          patientInfo={{
            ...patientInfo,
            patientId: props.match.params.patientId,
            visitId: props.match.params.visitId,
            roomName: assignedRoomDet?.roomName || "—",
            doctorName: currentDoctorName,
            ipopnumber: patientInfo?.ipopnumber || "—",
          }}
          regDate={regDate}
          parentCallBack={() => setSurgeryConsentPrint(false)}
        />
      )}
      {panchkarmaConsentPrint && (
        <Components.PrintPanchkarmaConsent
          patientInfo={{
            ...patientInfo,
            patientId: props.match.params.patientId,
            visitId: props.match.params.visitId,
            doctorName: currentDoctorName,
            ipopnumber: patientInfo?.ipopnumber || "—",
          }}
          regDate={regDate}
          parentCallBack={() => setPanchkarmaConsentPrint(false)}
        />
      )}
      {admissionConsentPrint && (
        <Components.PrintAdmissionConsent
          patientInfo={{
            ...patientInfo,
            patientId: props.match.params.patientId,
            visitId: props.match.params.visitId,
            doctorName: currentDoctorName,
            ipopnumber: patientInfo?.ipopnumber || "—",
          }}
          regDate={regDate}
          parentCallBack={() => setAdmissionConsentPrint(false)}
        />
      )}
      {painAssessmentPrint && (
        <Components.PrintPainAssessment
          patientInfo={{
            ...patientInfo,
            patientId: props.match.params.patientId,
            visitId: props.match.params.visitId,
            doctorName: currentDoctorName,
          }}
          regDate={regDate}
          parentCallBack={() => setPainAssessmentPrint(false)}
        />
      )}
    </main>
  );
}

export default PatientSections;
